// src/components/Dashboard.js
import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense, useContext, memo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Box,
    Container,
    Typography,
    IconButton,
    Badge,
    Divider,
    Button,
    Autocomplete,
    createFilterOptions,
    TextField,
    Select,
    MenuItem,
    Drawer,
    Paper,
    useTheme,
    useMediaQuery,
    Fab,
    Skeleton,
    CircularProgress,
    Chip,
    ToggleButtonGroup,
    ToggleButton,
    Avatar,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Slider,
    Switch,
    FormControlLabel,
    Collapse,
    InputAdornment,
    Alert,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    ShoppingCart as ShoppingCartIcon,
    AddShoppingCart as AddShoppingCartIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    FilterList as FilterListIcon,
    Close as CloseIcon,
    Inventory2Outlined as InventoryIcon,
    PhotoCameraBackOutlined as CardScanIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    DeleteOutline as DeleteIcon,
    ZoomIn as ZoomInIcon,
    ExpandMore as ExpandMoreIcon,
    Notes as NotesIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';

import { useCart } from '../contexts/CartContext';
import { ColorModeContext } from '../contexts/ThemeContext';
import { useKioskTheme } from '../contexts/KioskThemeContext';
import { searchProducts, fetchFilters, fetchFilterRegistry, suggestProducts, fetchCategories, fetchGroups, verifyKioskPin, verifyKioskUnlock, fetchKioskTheme, fetchBuylistConfig, fetchBuylistPrintings, isCanceledError } from '../api/products';
import { readSearchCorrection, formatSearchCorrection, searchTermForPage, suggestionFilter } from '../utils/searchCorrection';
import {
    FILTER_REGISTRY_FALLBACK,
    FILTER_GROUP_ORDER,
    LEGACY_RENDERED_KEYS,
    facetOptions,
    rangeBounds,
    visibleFiltersFor,
    buildDynamicParams,
    PRICE_FILTER_KEY,
    priceFilterVisible,
    priceRangeValue,
    parsePriceInput,
    formatRangeValue,
    appliesToGame,
} from '../utils/filterRegistry';
import deepEqual from '../utils/deepEqual';
import { joinLocations } from '../utils/locationList';
import { resolveBackImage, resolveDisplayImage } from '../utils/images';
import { cartLineImage, resolveCardArt, variantCatalogArt, variantScanImage } from '../utils/cardArt';
import { clampPreferencesToAllowed, preferredVariantForAdd } from '../utils/cartOptimizer';
import { variantKey, sameVariant } from '../utils/variantKey';
import { BUYLIST_CONDITIONS, buylistDefaultCondition, buylistDefaultPayment, buysAtCondition } from '../utils/buylistCustomerDefaults';
import { fetchBuylistQuotesByCondition, quoteForCondition } from '../utils/buylistConditionQuotes';
import { ladderFromQuote } from '../utils/buylistTierLadder';
import { AWAITING_STORE_PRICE_CAPTION, takesCardWithNoPrice } from '../utils/buylistTakesNoPrice';
import { gradedLineLabel, isGradedCategory } from '../utils/buylistGraded';
import GradedSellPicker, { SellTypeToggle, useGradedSell } from './GradedSellPicker';
import LoyaltyBalanceLookup from './LoyaltyBalanceLookup';
import ErrorBoundary from './ErrorBoundary';

const DetailDialog = lazy(() => import('./ProductDetailDialog'));

const CONFIG = {
    SEARCH_DEBOUNCE_MS: 300,
    SUGGEST_DEBOUNCE_MS: 200,
    PAGE_SIZE: 20,
    CARD_MIN_WIDTH: 300,
    DRAWER_WIDTH: 350,
    CART_DRAWER_WIDTH: 360,
    MAX_SUGGESTIONS: 8,
};

// How far below the screen the infinite-scroll sentinel counts as "reached":
// two screen heights. See useInfiniteScroll. TKT-2026-107279.
export const KIOSK_NEXT_PAGE_LEAD = '0px 0px 200% 0px';

const SORT_OPTIONS = [
    { value: 'name_asc', label: 'Name A→Z' },
    { value: 'name_desc', label: 'Name Z→A' },
    { value: 'price_low', label: 'Price ↑' },
    { value: 'price_high', label: 'Price ↓' },
    { value: 'newest', label: 'Newest' },
];

// A number, not a display string: every amount is formatted at the screen by the
// store's currency (KioskThemeContext formatMoney, TKT-2026-106987).
const getPrice = (product) => {
    if (!product?.prices?.length) return 0;
    const normal = product.prices.find((p) => p.subTypeName === 'Normal');
    const priceObj = normal || product.prices[0];
    return parseFloat(priceObj.midPrice ?? priceObj.marketPrice ?? priceObj.lowPrice) || 0;
};
const createFilterArray = (data, field) => [...new Set(data?.[field] || [])];

// Registry-keyed filter value model (TKT-2026-102569). chips/typeahead → string[],
// range → {min,max}, toggle → boolean. `printing` + `frame_effects` are legacy
// extras kept outside the registry.
const DEFAULT_FILTER_VALUES = {
    // universal / MTG enums (now multi-select, registry keys)
    rarity: [],
    color: [],
    colorIdentity: [],
    // legacy extras (kept, not registry-driven)
    printing: null,
    frame_effects: [],
};

// Migrate a previously-persisted filterValues object (old single-select shape +
// legacy keys: type/creatureType/legalStatuses) into the registry-keyed model.
const migrateFilterValues = (stored) => {
    if (!stored || typeof stored !== 'object') return { ...DEFAULT_FILTER_VALUES };
    const toArr = (v) => (Array.isArray(v) ? v : (v == null || v === '' ? [] : [v]));
    const next = {
        ...DEFAULT_FILTER_VALUES,
        rarity: toArr(stored.rarity),
        color: toArr(stored.color),
        colorIdentity: toArr(stored.colorIdentity),
        printing: stored.printing ?? null,
        frame_effects: Array.isArray(stored.frame_effects) ? stored.frame_effects : [],
    };
    // Old legacy keys → new registry keys (only if the new key isn't already set).
    if (stored.type && !next.cardType) next.cardType = toArr(stored.type);
    if (stored.creatureType && !next.creatureType) next.creatureType = toArr(stored.creatureType);
    if (stored.legalStatuses && !next.legality) next.legality = toArr(stored.legalStatuses);
    // Carry forward any already-registry-keyed values (ranges/toggles/chips).
    for (const [k, v] of Object.entries(stored)) {
        if (['rarity', 'color', 'colorIdentity', 'printing', 'frame_effects', 'type', 'creatureType', 'legalStatuses'].includes(k)) continue;
        if (next[k] === undefined) next[k] = v;
    }
    return next;
};
const createSuggestionOptions = (suggestions = []) => {
    const seen = new Set();

    return suggestions.reduce((acc, suggestion, index) => {
        const label = typeof suggestion === 'string'
            ? suggestion.trim()
            : String(suggestion?.label ?? suggestion?.name ?? '').trim();

        if (!label) return acc;

        const normalizedLabel = label.toLowerCase();
        if (seen.has(normalizedLabel)) return acc;

        seen.add(normalizedLabel);
        acc.push({ id: `${normalizedLabel}-${index}`, label });
        return acc;
    }, []);
};
// MUI's own filter, kept for typing ahead of the debounced suggest (utils/searchCorrection.js).
const defaultSuggestionFilter = createFilterOptions();

// ─────────────────────────────────────────────────────────────────────────────
// Custom hooks
// ─────────────────────────────────────────────────────────────────────────────
function useLocalStorageState(key, defaultValue) {
    const [state, setState] = useState(() => {
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : defaultValue;
        } catch {
            return defaultValue;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(key, JSON.stringify(state));
        } catch { }
    }, [key, state]);
    return [state, setState];
}

/**
 * Use as a state updater when a refetch returns data that may not have changed:
 * `setX((prev) => keepIfEqual(prev, next))`. Keeping the previous object keeps
 * every useMemo and useEffect downstream of it from re-running for nothing.
 * TKT-2026-105056
 */
const keepIfEqual = (prev, next) => (deepEqual(prev, next) ? prev : next);

/**
 * A key that identifies a search query by VALUE.
 *
 * Object keys are emitted in sorted order so two params objects describing the
 * same query always produce the same key, whatever order their properties were
 * assembled in. `undefined` values are dropped for the same reason axios drops
 * them: they are not part of the query. TKT-2026-105056
 */
function stableParamsKey(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value ?? null);
    if (Array.isArray(value)) return `[${value.map(stableParamsKey).join(',')}]`;
    return `{${Object.keys(value)
        .filter((k) => value[k] !== undefined)
        .sort()
        .map((k) => `${JSON.stringify(k)}:${stableParamsKey(value[k])}`)
        .join(',')}}`;
}

function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const h = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(h);
    }, [value, delay]);
    return debounced;
}

/**
 * Infinite scroll, subscribed ONCE per mount.
 *
 * The IntersectionObserver spec delivers an initial observation for every newly
 * observed target, so re-subscribing is not free: each rebuild re-answers "is
 * the sentinel on screen?", and while it is, that answer is a fresh instruction
 * to load another page.
 *
 * This hook used to list `callback` and `canLoadMore` as effect dependencies,
 * and the Dashboard passes an inline arrow for `callback`, so the subscription
 * was torn down and rebuilt on EVERY render. Loading a page re-rendered, which
 * rebuilt the observer, which fired again, which loaded another page. Paging
 * was driven by the render count rather than by the shopper scrolling, and
 * picking a filter is the worst moment to be in that loop: it resets to page 1,
 * which shortens the grid, which puts the sentinel back on screen and restarts
 * the whole cascade, while the page-1 search flips `loading` and swaps the
 * entire grid for skeletons. That is the flickering PunkOuter filmed.
 * TKT-2026-106404.
 *
 * Both moving parts are now read through refs at FIRE time, so the observer is
 * constructed once and only genuine intersection changes reach it. The last
 * effect covers the one case a single subscription would otherwise miss: a page
 * landed, the grid still does not reach the bottom of the screen, and the
 * shopper has nothing left to scroll. That asks the browser once per COMPLETED
 * PAGE, from real geometry, instead of once per render.
 *
 * Returns a CALLBACK REF to put on the sentinel, rather than taking a ref
 * object. The sentinel is rendered below the PIN-screen early return, so on a
 * locked kiosk it does not exist yet when effects first run. The old hook got
 * away with that only because it re-ran on every render and eventually found
 * the node; an effect that correctly runs once would have attached to nothing
 * and left infinite scroll dead for the rest of the session. A callback ref
 * attaches at the moment the node mounts, whenever that is.
 */
function useInfiniteScroll(callback, canLoadMore) {
    const callbackRef = useRef(callback);
    const canLoadMoreRef = useRef(canLoadMore);
    const observerRef = useRef(null);
    const nodeRef = useRef(null);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const attachSentinel = useCallback((node) => {
        if (typeof IntersectionObserver === 'undefined') return;
        if (!observerRef.current) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && canLoadMoreRef.current) {
                        // Claim the slot here rather than waiting for the
                        // caller's `canLoadMore` to go false. That value cannot
                        // change until React has re-rendered, and the observer
                        // can fire again before it does, which is how one page
                        // request became several.
                        canLoadMoreRef.current = false;
                        callbackRef.current();
                    }
                },
                // Ask for the next page while the shopper is still two
                // screens above the end of the grid. At '200px' the next 20
                // cards were requested only once the shopper reached the
                // bottom, so their pictures started downloading while they
                // were already on screen: measured on a 1080p wall panel over
                // shop Wi-Fi, 6 to 12% of the pictures in view were blank, up
                // to 12 at once. With this lead it was none, the same as the
                // storefront, which renders its whole page and lets the
                // browser's lazy loader start each picture well ahead.
                // A percentage is of the screen height, so the lead scales
                // with the panel. TKT-2026-107279.
                { rootMargin: KIOSK_NEXT_PAGE_LEAD },
            );
        }
        if (nodeRef.current) observerRef.current.unobserve(nodeRef.current);
        nodeRef.current = node;
        if (node) observerRef.current.observe(node);
    }, []);

    useEffect(() => () => {
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = null;
        nodeRef.current = null;
    }, []);

    useEffect(() => {
        const wasAllowed = canLoadMoreRef.current;
        canLoadMoreRef.current = canLoadMore;
        // Only on a false -> true edge, i.e. a page has just finished loading
        // and there is still more to fetch. Re-observing asks the browser for
        // the sentinel's CURRENT position, so this stops of its own accord as
        // soon as the grid is tall enough to push it past the fold.
        if (!canLoadMore || wasAllowed) return;
        if (observerRef.current && nodeRef.current) {
            observerRef.current.unobserve(nodeRef.current);
            observerRef.current.observe(nodeRef.current);
        }
    }, [canLoadMore]);

    return attachSentinel;
}

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────
const SearchBox = styled('div')(({ theme }) => ({
    flex: 1,
    width: '100%',
    minWidth: 0,
    maxWidth: 840,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.18),
    backdropFilter: 'blur(16px)',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
    padding: theme.spacing(0.75, 1.5),
    color: 'inherit',
    transition: 'background-color 0.2s, box-shadow 0.2s',
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.26),
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
    },
}));
const StyledSelect = styled(Select)(({ theme }) => ({
    color: 'inherit',
    '& .MuiSelect-icon': { color: 'inherit' },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.common.white, 0.5),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.common.white, 0.7),
    },
}));
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
    '& .MuiInputBase-root': {
        color: 'inherit',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.common.white, 0.5),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.common.white, 0.7),
    },
}));
const ProductGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${CONFIG.CARD_MIN_WIDTH}px, 1fr))`,
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    },
}));

const DashboardWrapper = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    paddingBottom: theme.spacing(6),
    background:
        theme.palette.background.ownerPage
        || (theme.palette.mode === 'light'
            ? 'linear-gradient(180deg, rgba(250,251,255,0.7) 0%, rgba(237,239,247,0.9) 60%, rgba(237,239,247,1) 100%)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.95) 0%, rgba(30,30,36,0.98) 65%, rgba(0,0,0,1) 100%)'),
}));

const HeroPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius + 2,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    background:
        theme.palette.background.ownerCard
        || (theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(237,239,247,0.92) 100%)'
            : 'linear-gradient(135deg, rgba(30,30,36,0.96) 0%, rgba(64,66,77,0.92) 100%)'),
    border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.24)}`,
    boxShadow: '0 22px 52px rgba(62,172,253,0.16)',
    [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'center',
    },
}));

const StatPill = styled(Box)(({ theme }) => ({
    minWidth: 160,
    borderRadius: 12,
    padding: theme.spacing(2.1, 1.8),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    background: alpha(theme.palette.common.white, theme.palette.surfaceIsDark ? 0.08 : 0.9),
    border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.2)}`,
    boxShadow: '0 14px 34px rgba(0,0,0,0.16)',
    [theme.breakpoints.between('sm', 'lg')]: {
        minWidth: 120,
        padding: theme.spacing(1.5, 1.4),
    },
}));

const FilterBlock = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2.4, 2.2),
    borderRadius: theme.shape.borderRadius * 0.75,
    background: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.06 : 0.12),
    border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.2)}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

// ─────────────────────────────────────────────────────────────────────────────
// PRICE FILTER (TKT-2026-105611)
// Two typed boxes rather than a slider: a store's span runs from cents to
// thousands, and no slider thumb lands on "$5 to $15" across that. The boxes
// keep a local draft so typing does not fire a search per keystroke; the value
// commits on blur, on Enter, or after a pause.
// ─────────────────────────────────────────────────────────────────────────────
const PRICE_COMMIT_DELAY_MS = 700;
const priceDraftText = (v) => (v === null || v === undefined ? '' : String(v));

const PriceRangeFilter = memo(({ value, bounds, currencySymbol, onChange, textFieldSx }) => {
    const { formatMoney } = useKioskTheme();
    const [draftMin, setDraftMin] = useState(priceDraftText(value?.min));
    const [draftMax, setDraftMax] = useState(priceDraftText(value?.max));

    // Follow outside changes (Clear All, removing the chip, a swapped pair). A
    // box whose text already means the stored amount is left alone, so a
    // commit mid-typing cannot turn "5." back into "5" under the shopper.
    useEffect(() => {
        const follow = (stored) => (d) => ((parsePriceInput(d) ?? null) === (stored ?? null) ? d : priceDraftText(stored));
        setDraftMin(follow(value?.min));
        setDraftMax(follow(value?.max));
    }, [value?.min, value?.max]);

    const commit = useCallback(() => {
        const next = priceRangeValue(draftMin, draftMax);
        const same = (next === null && !value) || (next && value && next.min === value.min && next.max === value.max);
        if (!same) onChange(PRICE_FILTER_KEY, next);
    }, [draftMin, draftMax, value, onChange]);

    useEffect(() => {
        const t = setTimeout(commit, PRICE_COMMIT_DELAY_MS);
        return () => clearTimeout(t);
    }, [commit]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter') commit();
    };
    const adornment = { startAdornment: <InputAdornment position='start'>{currencySymbol}</InputAdornment> };
    const fmt = (n) => formatMoney(n);

    return (
        <FilterBlock>
            <Typography variant='subtitle2' fontWeight='medium'>Price</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    size='small'
                    placeholder='Min'
                    value={draftMin}
                    onChange={(e) => setDraftMin(e.target.value)}
                    onBlur={commit}
                    onKeyDown={onKeyDown}
                    InputProps={adornment}
                    inputProps={{ inputMode: 'decimal', 'aria-label': 'Minimum price' }}
                    sx={{ ...textFieldSx, flex: 1 }}
                />
                <Typography variant='body2' color='text.secondary'>to</Typography>
                <TextField
                    size='small'
                    placeholder='Max'
                    value={draftMax}
                    onChange={(e) => setDraftMax(e.target.value)}
                    onBlur={commit}
                    onKeyDown={onKeyDown}
                    InputProps={adornment}
                    inputProps={{ inputMode: 'decimal', 'aria-label': 'Maximum price' }}
                    sx={{ ...textFieldSx, flex: 1 }}
                />
            </Box>
            {bounds && (
                <Typography variant='caption' color='text.secondary'>
                    In stock from {fmt(bounds.min)} to {fmt(bounds.max)}
                </Typography>
            )}
        </FilterBlock>
    );
});
PriceRangeFilter.propTypes = {
    value: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
    bounds: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
    currencySymbol: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    textFieldSx: PropTypes.object,
};

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT CARD
// ─────────────────────────────────────────────────────────────────────────────
const ProductCard = memo(({ product, showCardScans, stockDisplayMode = 'exact', onAdd, onProductClick, onImageClick }) => {
    const theme = useTheme();
    const { cart, cartPreferences } = useCart();
    const { formatMoney, themeOverrides } = useKioskTheme();
    const showCardLocations = themeOverrides?.showCardLocations === true;
    const [selectedSku, setSelectedSku] = useState('');
    const [sortedVariants, setSortedVariants] = useState([]);

    // TKT-2026-105225: a preference set on the cart page also governs what the
    // next Add adds, so a shopper who asked for the cheapest copy does not have
    // to re-apply it after every card. Anything the store has withheld is
    // stripped here, so an owner turning an axis off takes effect immediately.
    const cartPrefs = useMemo(
        () => clampPreferencesToAllowed(cartPreferences, themeOverrides?.cartOptimization),
        [cartPreferences, themeOverrides?.cartOptimization],
    );

    useEffect(() => {
        if (!product.variants) return;
        const conditionOrder = { NM: 0, LP: 1, MP: 2, HP: 3, DM: 4 };
        const sorted = [...product.variants].sort((a, b) => {
            const condA = conditionOrder[a.condAbbr] ?? 5;
            const condB = conditionOrder[b.condAbbr] ?? 5;
            if (condA !== condB) return condA - condB;
            const printA = a.printing === 'Normal' ? 0 : 1;
            const printB = b.printing === 'Normal' ? 0 : 1;
            return printA - printB;
        });
        setSortedVariants(sorted);
        // With no preference expressed this is sorted[0], which is what the tile
        // has always defaulted to.
        const preferred = preferredVariantForAdd(sorted, cartPrefs, sorted[0]);
        // Selection is held as a variantKey, not a skuId. One skuId can now be two
        // entries in this list (a graded slab and a raw copy), so a skuId would
        // both duplicate the React key and select the wrong one of the two.
        // TKT-2026-105367.
        setSelectedSku(variantKey(preferred) || '');
    }, [product.variants, cartPrefs]);

    const selectedVariant = useMemo(() => sortedVariants.find((v) => variantKey(v) === selectedSku) || null, [selectedSku, sortedVariants]);
    const cardImage = useMemo(
        () => resolveDisplayImage(showCardScans, selectedVariant, product),
        [showCardScans, selectedVariant, product],
    );
    // The tile itself always shows the front. The back is handed to the enlarge
    // view, which can turn the card over. TKT-2026-105491
    const cardBackImage = useMemo(
        () => resolveBackImage(showCardScans, selectedVariant),
        [showCardScans, selectedVariant],
    );

    const inCartQty = useMemo(() => {
        if (!selectedVariant) return 0;
        // Count only THIS copy. Matching on skuId counted a slab already in the
        // cart against a raw copy's availability, which then refused an Add for
        // stock the shopper had not taken. TKT-2026-105367.
        return cart.filter((item) => sameVariant(item.variant, selectedVariant)).reduce((sum, i) => sum + i.qty, 0);
    }, [cart, selectedVariant]);

    const availableQty = selectedVariant ? selectedVariant.quantity - inCartQty : 0;

    const displayPrice = useMemo(() => {
        if (selectedVariant?.price != null) {
            return parseFloat(selectedVariant.price);
        }
        return getPrice(product);
    }, [selectedVariant, product]);

    const handleVariantChange = useCallback((e) => {
        e.stopPropagation();
        setSelectedSku(e.target.value);
    }, []);
    const handleAddClick = useCallback(
        (e) => {
            e.stopPropagation();
            if (!selectedVariant || availableQty < 1) return;
            onAdd(product, selectedVariant);
        },
        [onAdd, product, selectedVariant, availableQty],
    );
    const handleProductClick = useCallback(() => {
        onProductClick(product);
    }, [onProductClick, product]);

    return (
        <Paper
            onClick={handleProductClick}
            role='button'
            tabIndex={0}
            elevation={0}
            sx={{
                cursor: 'pointer',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                background:
                    theme.palette.background.ownerCard
                    || (theme.palette.mode === 'light'
                        ? 'linear-gradient(155deg, rgba(255,255,255,0.96) 0%, rgba(237,239,247,0.94) 100%)'
                        : 'linear-gradient(155deg, rgba(30,30,36,0.96) 0%, rgba(64,66,77,0.94) 100%)'),
                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.28)}`,
                boxShadow: '0 20px 48px rgba(62,172,253,0.18)',
                transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                '@media (hover: hover)': {
                    '&:hover': {
                        transform: 'translateY(-8px) scale(1.01)',
                        boxShadow: '0 32px 72px rgba(62,172,253,0.28)',
                    },
                },
                '&:active': {
                    transform: 'scale(0.98)',
                },
            }}
            onKeyDown={(e) => {
                if (['Enter', ' '].includes(e.key)) {
                    e.preventDefault();
                    handleProductClick();
                }
            }}
            aria-label={`View details for ${product.cleanName}`}
        >
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    onImageClick(cardImage, product.cleanName, cardBackImage);
                }}
                sx={{
                    position: 'relative',
                    pt: '65%',
                    overflow: 'hidden',
                    cursor: 'zoom-in',
                    background:
                        theme.palette.mode === 'light'
                            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.35)} 0%, ${alpha(theme.palette.secondary.main, 0.28)} 100%)`
                            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.22)} 100%)`,
                }}
            >
                {cardImage ? (
                    <Box
                        component='img'
                        src={cardImage}
                        alt={product.cleanName}
                        loading='lazy'
                        sx={{
                            position: 'absolute',
                            inset: 12,
                            width: 'calc(100% - 24px)',
                            height: 'calc(100% - 24px)',
                            objectFit: 'contain',
                            filter: theme.palette.mode === 'light' ? 'drop-shadow(0 12px 26px rgba(0,0,0,0.18))' : 'drop-shadow(0 12px 26px rgba(62,172,253,0.32))',
                        }}
                        onError={(e) => {
                            e.currentTarget.style.opacity = '0';
                        }}
                    />
                ) : null}
                <IconButton
                    size='small'
                    aria-label={`Enlarge image for ${product.cleanName}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick(cardImage, product.cleanName, cardBackImage);
                    }}
                    sx={{
                        position: 'absolute',
                        right: 12,
                        bottom: 12,
                        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.82 : 0.42),
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.96 : 0.62),
                        },
                    }}
                >
                    <ZoomInIcon fontSize='small' />
                </IconButton>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                    }}
                >
                    <Chip
                        label={product.categoryName}
                        size='small'
                        sx={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: theme.palette.primary.contrastText,
                            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.85 : 0.68),
                            borderColor: 'transparent',
                            // No backdropFilter: one per card was a GPU blur pass per
                            // card per scroll frame, over a smooth gradient where it
                            // changed nothing. TKT-2026-107279.
                        }}
                    />
                    {product.groupAbbreviation && (
                        <Chip
                            label={product.groupAbbreviation}
                            size='small'
                            variant='outlined'
                            sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                // The set tag is the store's primary colour painted
                                // AS TEXT on a card, so a store whose primary is
                                // unreadable there needs the derived foreground,
                                // not a lighten/darken of the raw colour.
                                // primary.light on a near-black primary is
                                // #5d6164, which read at 2.4:1 for d20dungeon.
                                // Every other store keeps the shade it has today.
                                // TKT-2026-105169.
                                color: theme.palette.primary.needsAccent
                                    ? theme.palette.primary.onSurface
                                    : (theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.light),
                                borderColor: alpha(
                                    theme.palette.primary.needsAccent ? theme.palette.primary.onSurface : theme.palette.primary.main,
                                    theme.palette.mode === 'light' ? 0.55 : 0.6,
                                ),
                                backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.85 : 0.32),
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
                <Typography
                    variant='subtitle1'
                    sx={{
                        flexGrow: 1,
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                    title={product.cleanName}
                >
                    {product.cleanName}
                </Typography>

                {/* TKT-2026-103353: physical bin/showcase location under the name so
                    shoppers can self-locate the card in the store's binders without
                    opening the card. Reuses the Show Card Locations owner setting. */}
                {showCardLocations && selectedVariant?.locations?.length > 0 && (
                    <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mt: -1, lineHeight: 1.3, wordBreak: 'break-word' }}
                        title={joinLocations(selectedVariant.locations)}
                    >
                        {selectedVariant.locations.length > 1 ? 'Locations: ' : 'Location: '}
                        {joinLocations(selectedVariant.locations)}
                    </Typography>
                )}

                {sortedVariants.length > 0 && (
                    <FormControl size='small' fullWidth sx={{ mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <InputLabel id={`variant-label-${product.productId}`}>Variant</InputLabel>
                        <Select
                            labelId={`variant-label-${product.productId}`}
                            value={selectedSku}
                            label='Variant'
                            onChange={handleVariantChange}
                            sx={{
                                borderRadius: 10,
                                '& .MuiSelect-select': { borderRadius: 10 },
                            }}
                        >
                            {sortedVariants.map((v) => (
                                <MenuItem key={variantKey(v)} value={variantKey(v)}>
                                    {v.condAbbr} / {v.printing}
                                    {v.langAbbr && v.langAbbr !== 'EN' ? ` / ${v.langAbbr}` : ''} — {formatMoney(v.price)}
                                    {stockDisplayMode === 'exact' && v.quantity > 0 ? ` (${v.quantity} left)` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant='h5' color='primary.onSurface' fontWeight='bold'>
                        {formatMoney(displayPrice)}
                    </Typography>
                    {/* TKT-2026-103353: owner controls how stock is shown. 'exact' shows
                        the number, 'in_stock' shows just In stock, 'hidden' shows nothing.
                        The Add-to-cart availability logic below is unaffected. */}
                    {stockDisplayMode !== 'hidden' && availableQty > 0 && (
                        <Typography variant='caption' color='success.main' fontWeight={600}>
                            {stockDisplayMode === 'in_stock' ? 'In stock' : `${availableQty} in stock`}
                        </Typography>
                    )}
                </Box>

                <Button
                    variant='contained'
                    onClick={handleAddClick}
                    aria-label={`Add ${product.cleanName} to cart`}
                    disabled={!selectedVariant || availableQty < 1}
                    startIcon={availableQty > 0 ? <AddShoppingCartIcon /> : null}
                    sx={(theme) => ({
                        mt: 'auto',
                        borderRadius: 10,
                        py: { xs: 1.1, sm: 1.4, lg: 1.1 },
                        minHeight: 44,
                        fontWeight: 600,
                        backgroundImage: 'none',
                        backgroundColor: availableQty > 0 ? theme.palette.primary.main : alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.08 : 0.22),
                        color: availableQty > 0 ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                        boxShadow: availableQty > 0 ? `0 18px 30px ${alpha(theme.palette.primary.main, 0.22)}` : 'none',
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease',
                        '@media (hover: hover)': {
                            '&:hover': availableQty > 0
                                ? {
                                    backgroundColor: theme.palette.primary.dark,
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 24px 40px ${alpha(theme.palette.primary.main, 0.3)}`,
                                }
                                : undefined,
                        },
                        '&.Mui-disabled': {
                            backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.06 : 0.18),
                            color: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.4 : 0.5),
                        },
                    })}
                >
                    {availableQty > 0 ? 'Add to cart' : 'Out of stock'}
                </Button>
            </Box>
        </Paper>
    );
});
ProductCard.propTypes = {
    product: PropTypes.object.isRequired,
    showCardScans: PropTypes.bool,
    stockDisplayMode: PropTypes.oneOf(['exact', 'in_stock', 'hidden']),
    onAdd: PropTypes.func.isRequired,
    onProductClick: PropTypes.func.isRequired,
    onImageClick: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
// BUYLIST PRODUCT CARD (for "Sell To Us" mode)
// ─────────────────────────────────────────────────────────────────────────────
// Simple cache to avoid redundant API calls
const buylistCache = {
    printings: new Map(), // key: productId -> { printings: ['Normal', 'Foil'], languages: ['EN', 'JP'] }
    prices: new Map(),    // key: `${productId}-${printing}-${language}` -> { at, data }, data = every condition's own quote
};

// A kiosk page stays open all day, and the buylist re-quotes every line when
// the trade-in is submitted. A price held for the life of the page is a quote
// the store may no longer make, so it is held for ten minutes (TKT-2026-106839).
const BUYLIST_PRICE_TTL_MS = 10 * 60 * 1000;
const cachedBuylistPrice = (key) => {
    const hit = buylistCache.prices.get(key);
    if (!hit) return null;
    if (Date.now() - hit.at > BUYLIST_PRICE_TTL_MS) {
        buylistCache.prices.delete(key);
        return null;
    }
    return hit.data;
};
// An empty answer is not cached, so a failed fetch is asked again next time.
const cacheBuylistPrice = (key, data) => {
    if (data) buylistCache.prices.set(key, { at: Date.now(), data });
};

export const BuylistProductCard = memo(({ product, onAdd, onProductClick, storeName, onImageClick, buylistGameSettings, gradedEnabled = false }) => {
    const theme = useTheme();
    const { formatMoney } = useKioskTheme();

    // The store's own "Preselect a condition" and "Preselect cash or store
    // credit", read from the per-game block the buylist site reads. These were
    // the literals 'NM' and 'Cash', so a store that buys only LP opened every
    // card on "Not Buying" (TKT-2026-106337).
    const defaultCondition = useMemo(
        () => buylistDefaultCondition(buylistGameSettings, product?.categoryId),
        [buylistGameSettings, product?.categoryId]
    );
    const defaultPayment = useMemo(
        () => buylistDefaultPayment(buylistGameSettings, product?.categoryId),
        [buylistGameSettings, product?.categoryId]
    );

    // State for selections
    const [selectedPrinting, setSelectedPrinting] = useState('Normal');
    const [selectedCondition, setSelectedCondition] = useState(defaultCondition);
    const [selectedLanguage, setSelectedLanguage] = useState('EN');
    const [paymentType, setPaymentType] = useState(defaultPayment);

    // The config request can land after the card has mounted (a kiosk reloaded
    // straight into Sell To Us). Follow the store's default until the shopper
    // makes a choice of their own, and never override that choice.
    const conditionPicked = useRef(false);
    const paymentPicked = useRef(false);
    useEffect(() => {
        if (!conditionPicked.current) setSelectedCondition(defaultCondition);
    }, [defaultCondition]);
    useEffect(() => {
        if (!paymentPicked.current) setPaymentType(defaultPayment);
    }, [defaultPayment]);
    const pickCondition = useCallback((condition) => {
        conditionPicked.current = true;
        setSelectedCondition(condition);
    }, []);
    const pickPayment = useCallback((payment) => {
        paymentPicked.current = true;
        setPaymentType(payment);
    }, []);
    const [availablePrintings, setAvailablePrintings] = useState(['Normal']);
    const [availableLanguages, setAvailableLanguages] = useState(['EN']);
    const [loadingPrintings, setLoadingPrintings] = useState(false);
    const [buylistPrices, setBuylistPrices] = useState(null);
    const [loadingPrice, setLoadingPrice] = useState(false);
    // Gate the price fetch until the real printings list is known, so we never
    // price a guessed 'Normal' for a printing-only card (TKT-2026-103462).
    const [printingsLoaded, setPrintingsLoaded] = useState(false);

    // Fetch printings and languages (cached)
    useEffect(() => {
        if (!product?.productId || !storeName) return;
        const cacheKey = product.productId;
        if (buylistCache.printings.has(cacheKey)) {
            const cached = buylistCache.printings.get(cacheKey);
            // Handle both old format (string array) and new format ({ printings, languages })
            if (Array.isArray(cached)) {
                setAvailablePrintings(cached);
                setAvailableLanguages(['EN']);
            } else {
                setAvailablePrintings(cached.printings || ['Normal']);
                setAvailableLanguages(cached.languages || ['EN']);
            }
            setPrintingsLoaded(true);
            return;
        }
        let cancelled = false;
        setPrintingsLoaded(false);
        const fetchPrintings = async () => {
            setLoadingPrintings(true);
            try {
                const response = await fetchBuylistPrintings(storeName, product.productId);
                if (cancelled) return;
                const raw = response.data?.availablePrintings || response.data || ['Normal'];
                let printingResult = ['Normal'];
                if (Array.isArray(raw)) {
                    const names = raw.map(p => typeof p === 'string' ? p : p?.printing || 'Normal');
                    printingResult = [...new Set(names)].filter(Boolean);
                    if (printingResult.length === 0) printingResult = ['Normal'];
                }

                // Read the canonical availableLanguages field straight from the
                // API. Deriving it from the printings array client-side was the
                // path that let printing tokens leak into langAbbr (TKT-2026-100378).
                const apiLanguages = Array.isArray(response.data?.availableLanguages)
                    ? response.data.availableLanguages.filter(Boolean)
                    : [];
                const languageResult = apiLanguages.length > 0 ? apiLanguages : ['EN'];

                buylistCache.printings.set(cacheKey, { printings: printingResult, languages: languageResult });
                setAvailablePrintings(printingResult);
                setAvailableLanguages(languageResult);
            } catch (e) {
                if (cancelled) return;
                setAvailablePrintings(['Normal']);
                setAvailableLanguages(['EN']);
            } finally {
                if (!cancelled) {
                    setLoadingPrintings(false);
                    setPrintingsLoaded(true);
                }
            }
        };
        fetchPrintings();
        return () => { cancelled = true; };
    }, [product?.productId, storeName]);

    // Auto-correct printing if current selection isn't available
    // (e.g. product only has 'Foil', but selectedPrinting is still default 'Normal')
    useEffect(() => {
        if (availablePrintings.length > 0 && !availablePrintings.includes(selectedPrinting)) {
            setSelectedPrinting(availablePrintings[0]);
        }
    }, [availablePrintings, selectedPrinting]);

    // Auto-select language: prefer 'EN' if available, otherwise pick the first available
    useEffect(() => {
        if (availableLanguages.length > 0 && !availableLanguages.includes(selectedLanguage)) {
            setSelectedLanguage(availableLanguages.includes('EN') ? 'EN' : availableLanguages[0]);
        }
    }, [availableLanguages, selectedLanguage]);

    // Fetch prices once per printing+language (cached), every condition asked AT
    // that condition in one call. An NM answer's LP figure is not the store's LP
    // offer, and the buylist refuses a trade-in priced from it (TKT-2026-106839).
    // Mirrors buylist frontend: if initial printing has no price, try alternates
    useEffect(() => {
        // Wait for the real printings, and only price a printing the product
        // actually has. Otherwise a guessed 'Normal' fetch returns zeros and,
        // arriving out of order, clobbers the correct prices (TKT-2026-103462).
        if (!product?.productId || !storeName || !printingsLoaded || !availablePrintings.includes(selectedPrinting)) return;
        const cacheKey = `${product.productId}-${selectedPrinting}-${selectedLanguage}`;
        const cachedPrices = cachedBuylistPrice(cacheKey);
        if (cachedPrices) {
            setBuylistPrices(cachedPrices);
            return;
        }
        let cancelled = false;
        const fetchPrice = async () => {
            setLoadingPrice(true);
            try {
                let priceData = await fetchBuylistQuotesByCondition(storeName, {
                    productId: product.productId,
                    printing: selectedPrinting,
                    language: selectedLanguage
                });
                // Each printing's key holds that printing's own answer. It held
                // whichever alternate the fallback below settled on, so going
                // back to this printing showed, and added, the other one's price.
                cacheBuylistPrice(cacheKey, priceData);

                // Is this printing bought at the condition the card opens on?
                // This was keyed to NM, so for a store that does not buy NM
                // every printing looked unbought and none was ever picked.
                const hasPositivePrice = buysAtCondition(priceData, defaultCondition);

                // If no positive price and there are alternate printings, try them
                // (matches buylist frontend fallback logic)
                if (!hasPositivePrice && availablePrintings.length > 1) {
                    const alternates = availablePrintings.filter(p => p !== selectedPrinting);
                    for (const altPrinting of alternates) {
                        if (cancelled) break;
                        const altCacheKey = `${product.productId}-${altPrinting}-${selectedLanguage}`;
                        const cached = cachedBuylistPrice(altCacheKey);
                        if (cached) {
                            if (buysAtCondition(cached, defaultCondition)) {
                                priceData = cached;
                                if (!cancelled) setSelectedPrinting(altPrinting);
                                break;
                            }
                            continue;
                        }
                        try {
                            const altData = await fetchBuylistQuotesByCondition(storeName, {
                                productId: product.productId,
                                printing: altPrinting,
                                language: selectedLanguage
                            });
                            cacheBuylistPrice(altCacheKey, altData);
                            if (buysAtCondition(altData, defaultCondition)) {
                                priceData = altData;
                                if (!cancelled) setSelectedPrinting(altPrinting);
                                break;
                            }
                        } catch (altErr) {
                            // Skip this alternate, try next
                        }
                    }
                }

                if (cancelled) return;
                setBuylistPrices(priceData);
            } catch (e) {
                if (cancelled) return;
                setBuylistPrices(null);
            } finally {
                if (!cancelled) setLoadingPrice(false);
            }
        };
        fetchPrice();
        return () => { cancelled = true; };
    }, [availablePrintings, product?.productId, storeName, selectedPrinting, selectedLanguage, printingsLoaded, defaultCondition]);

    // The whole answer asked at the selected condition, for the fields that
    // belong to one condition (the bulk-rate marker).
    const conditionQuote = useMemo(
        () => quoteForCondition(buylistPrices, selectedCondition) || buylistPrices,
        [buylistPrices, selectedCondition]
    );

    // Get prices for selected condition
    const cashPrice = useMemo(() => {
        if (!buylistPrices) return 0;
        return parseFloat(buylistPrices[`${selectedCondition}FinalCash`]) || 0;
    }, [buylistPrices, selectedCondition]);

    const creditPrice = useMemo(() => {
        if (!buylistPrices) return 0;
        return parseFloat(buylistPrices[`${selectedCondition}FinalCredit`]) || 0;
    }, [buylistPrices, selectedCondition]);



    const hasBuyPrice = cashPrice > 0 || creditPrice > 0;
    // TKT-2026-108714: a store that takes this game's cards with no price.
    const takesNoPrice = !hasBuyPrice && takesCardWithNoPrice(
        quoteForCondition(buylistPrices, selectedCondition),
        buylistGameSettings?.[product?.categoryId],
        { condition: selectedCondition, language: selectedLanguage, sealed: product?.sealed === true },
    );

    const handleAddClick = useCallback((e) => {
        e.stopPropagation();
        if (!hasBuyPrice && !takesNoPrice) return;
        onAdd(product, {
            skuId: `${selectedPrinting}-${selectedCondition}`,
            printing: selectedPrinting,
            condAbbr: selectedCondition,
            langAbbr: selectedLanguage
        }, {
            mode: 'buy',
            paymentType,
            buylistCashPrice: cashPrice,
            buylistCreditPrice: creditPrice,
            awaitingStorePrice: takesNoPrice,
            // Only from the answer asked at this condition (TKT-2026-107051).
            buylistLadder: ladderFromQuote(quoteForCondition(buylistPrices, selectedCondition)),
        });
    }, [onAdd, product, selectedPrinting, selectedCondition, selectedLanguage, paymentType, cashPrice, creditPrice, hasBuyPrice, takesNoPrice, buylistPrices]);

    // A slab of this card, on a game whose "Graded slabs" is on (TKT-2026-107322).
    // Off, nothing below renders and the card is the raw card it always was.
    const [sellType, setSellType] = useState('raw');
    const isGradedMode = gradedEnabled && sellType === 'graded';
    const graded = useGradedSell({
        storeName,
        productId: product?.productId,
        printing: selectedPrinting,
        language: selectedLanguage,
        active: isGradedMode && printingsLoaded && availablePrintings.includes(selectedPrinting),
        gameSettings: buylistGameSettings,
        categoryId: product?.categoryId,
    });
    const { buildLine: buildGradedLine, setCertNumber: setGradedCert } = graded;
    const handleGradedAddClick = useCallback((e) => {
        e.stopPropagation();
        const line = buildGradedLine(paymentType);
        if (!line) return;
        onAdd(product, line.variant, line.options);
        // A cert belongs to one slab: the next one is typed fresh.
        setGradedCert('');
    }, [buildGradedLine, setGradedCert, onAdd, product, paymentType]);

    const shownCash = isGradedMode ? graded.offer.cash : cashPrice;
    const shownCredit = isGradedMode ? graded.offer.credit : creditPrice;
    const shownExtraDigit = isGradedMode ? false : Boolean(conditionQuote?.bulkOverrideApplied);
    const shownNoPrice = !isGradedMode && takesNoPrice;
    const canSell = isGradedMode ? graded.offered : hasBuyPrice || takesNoPrice;
    const sellLoading = isGradedMode ? graded.loading : loadingPrice;
    const notSellingLabel = isGradedMode ? 'Not Offered' : 'Not Buying';

    const handleProductClick = useCallback(() => {
        onProductClick(product);
    }, [onProductClick, product]);

    // The art for the printing and language being sold, not the productId's one
    // render: for a WOTC-era Pokemon card that is the 1st Edition photo, so an
    // Unlimited trade-in showed the wrong card. Until the real printings arrive
    // the 'Normal' default is a guess, so no printing is asked for. TKT-2026-107421
    const cardArt = useMemo(
        () => resolveCardArt(product, selectedLanguage, printingsLoaded ? selectedPrinting : null) || null,
        [product, selectedLanguage, selectedPrinting, printingsLoaded],
    );

    return (
        <Paper
            onClick={handleProductClick}
            role='button'
            tabIndex={0}
            elevation={0}
            sx={{
                cursor: 'pointer',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                background: theme.palette.background.ownerCard
                    || (theme.palette.mode === 'light'
                        ? 'linear-gradient(155deg, rgba(255,255,255,0.96) 0%, rgba(237,247,239,0.94) 100%)'
                        : 'linear-gradient(155deg, rgba(30,36,30,0.96) 0%, rgba(64,77,66,0.94) 100%)'),
                border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.25 : 0.35)}`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '@media (hover: hover)': {
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 48px ${alpha(theme.palette.success.main, 0.2)}` },
                },
                '&:active': { transform: 'scale(0.98)' },
            }}
            onKeyDown={(e) => ['Enter', ' '].includes(e.key) && (e.preventDefault(), handleProductClick())}
            aria-label={`Sell ${product.cleanName}`}
        >
            {/* Image */}
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    onImageClick(cardArt, product.cleanName);
                }}
                sx={{ position: 'relative', pt: '50%', overflow: 'hidden', background: alpha(theme.palette.success.main, 0.08), cursor: 'zoom-in' }}
            >
                {cardArt ? (
                    <Box
                        component='img'
                        src={cardArt}
                        alt={product.cleanName}
                        loading='lazy'
                        sx={{ position: 'absolute', inset: 8, width: 'calc(100% - 16px)', height: 'calc(100% - 16px)', objectFit: 'contain' }}
                        onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                    />
                ) : null}
                <IconButton
                    size='small'
                    aria-label={`Enlarge image for ${product.cleanName}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick(cardArt, product.cleanName);
                    }}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        bottom: 8,
                        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.82 : 0.42),
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.96 : 0.62),
                        },
                    }}
                >
                    <ZoomInIcon fontSize='small' />
                </IconButton>
                {/* Left side: Sell To Us badge */}
                <Chip label="Sell To Us" size='small' sx={{ position: 'absolute', top: 8, left: 8, fontSize: '0.6rem', fontWeight: 700, color: theme.palette.success.contrastText, backgroundColor: theme.palette.success.main }} />

                {/* Right side: Hotlist / Darklist / Bulk chips */}
                {(product.isOnHotlist || buylistPrices?.isOnHotlist) && (
                    <Chip
                        size='small'
                        label={`Hotlist${(product.hotlistBoostPercentage || buylistPrices?.hotlistBoostPercentage) > 0 ? ` +${product.hotlistBoostPercentage || buylistPrices?.hotlistBoostPercentage}%` : ''}`}
                        color="success"
                        sx={{ position: 'absolute', top: 8, right: 8, fontSize: '0.55rem', fontWeight: 700, height: 18 }}
                    />
                )}
                {(product.isOnDarklist || buylistPrices?.isOnDarklist) && (
                    <Chip
                        size='small'
                        label={`Darklist${(product.darklistPenaltyPercentage || buylistPrices?.darklistPenaltyPercentage) > 0 ? ` -${product.darklistPenaltyPercentage || buylistPrices?.darklistPenaltyPercentage}%` : ''}`}
                        color="error"
                        sx={{ position: 'absolute', top: (product.isOnHotlist || buylistPrices?.isOnHotlist) ? 30 : 8, right: 8, fontSize: '0.55rem', fontWeight: 700, height: 18 }}
                    />
                )}
                {!isGradedMode && conditionQuote?.bulkOverrideApplied && (
                    <Chip
                        size='small'
                        label="Bulk"
                        color="info"
                        sx={{
                            position: 'absolute',
                            top: (product.isOnHotlist || buylistPrices?.isOnHotlist)
                                ? (product.isOnDarklist || buylistPrices?.isOnDarklist) ? 52 : 30
                                : (product.isOnDarklist || buylistPrices?.isOnDarklist) ? 30 : 8,
                            right: 8,
                            fontSize: '0.55rem',
                            fontWeight: 700,
                            height: 18
                        }}
                    />
                )}
            </Box>

            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1 }}>
                {/* Name */}
                <Typography variant='body2' sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.2, minHeight: '2.4em' }} title={product.cleanName}>
                    {product.cleanName}
                </Typography>

                {/* Printing toggle */}
                {!loadingPrintings && availablePrintings.length > 1 && (
                    <Box onClick={(e) => e.stopPropagation()}>
                        <ToggleButtonGroup value={selectedPrinting} exclusive onChange={(_, v) => v && setSelectedPrinting(v)} size='small' fullWidth sx={{ '& .MuiToggleButton-root': { fontSize: '0.7rem', py: 0.5, minHeight: 32, fontWeight: 600 }, '& .Mui-selected': { backgroundColor: `${theme.palette.success.main} !important`, color: `${theme.palette.success.contrastText} !important` } }}>
                            {availablePrintings.map(p => <ToggleButton key={p} value={p}>{p}</ToggleButton>)}
                        </ToggleButtonGroup>
                    </Box>
                )}

                {/* Raw or Graded, only where the game buys slabs */}
                {gradedEnabled && <SellTypeToggle value={sellType} onChange={setSellType} compact />}

                {isGradedMode ? (
                    <GradedSellPicker graded={graded} paymentType={paymentType} compact />
                ) : (
                    /* Condition toggle */
                    <Box onClick={(e) => e.stopPropagation()}>
                        <ToggleButtonGroup value={selectedCondition} exclusive onChange={(_, v) => v && pickCondition(v)} size='small' fullWidth sx={{ '& .MuiToggleButton-root': { fontSize: '0.65rem', py: 0.5, px: 0.5, minWidth: 0, minHeight: 32, fontWeight: 600 }, '& .Mui-selected': { backgroundColor: `${theme.palette.success.main} !important`, color: `${theme.palette.success.contrastText} !important` } }}>
                            {BUYLIST_CONDITIONS.map(c => <ToggleButton key={c} value={c}>{c}</ToggleButton>)}
                        </ToggleButtonGroup>
                    </Box>
                )}

                {shownNoPrice && (
                    <Typography variant='caption' color='text.secondary' sx={{ mt: 'auto' }}>
                        {AWAITING_STORE_PRICE_CAPTION}
                    </Typography>
                )}

                {/* Cash / Credit chips - both visible */}
                <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 0.5, mt: shownNoPrice ? 0 : 'auto' }}>
                    <Chip
                        label={shownNoPrice ? 'Cash' : `Cash: ${formatMoney(shownCash, { extraDigit: shownExtraDigit })}`}
                        size='small'
                        onClick={() => pickPayment('Cash')}
                        sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            backgroundColor: paymentType === 'Cash' ? '#2196F3' : alpha('#2196F3', 0.15),
                            color: paymentType === 'Cash' ? '#fff' : '#2196F3',
                            '&:hover': { backgroundColor: paymentType === 'Cash' ? '#1976D2' : alpha('#2196F3', 0.25) }
                        }}
                    />
                    <Chip
                        label={shownNoPrice ? 'Credit' : `Credit: ${formatMoney(shownCredit, { extraDigit: shownExtraDigit })}`}
                        size='small'
                        onClick={() => pickPayment('Credit')}
                        sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            backgroundColor: paymentType === 'Credit' ? '#9C27B0' : alpha('#9C27B0', 0.15),
                            color: paymentType === 'Credit' ? '#fff' : '#9C27B0',
                            '&:hover': { backgroundColor: paymentType === 'Credit' ? '#7B1FA2' : alpha('#9C27B0', 0.25) }
                        }}
                    />
                </Box>

                {/* Sell button */}
                <Button
                    variant='contained'
                    onClick={isGradedMode ? handleGradedAddClick : handleAddClick}
                    disabled={!canSell || sellLoading}
                    sx={{
                        borderRadius: 2,
                        py: 0.7,
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        backgroundColor: canSell ? theme.palette.success.main : alpha(theme.palette.text.primary, 0.1),
                        color: canSell ? theme.palette.success.contrastText : theme.palette.text.secondary,
                        '&:hover': canSell ? { backgroundColor: theme.palette.success.dark } : undefined,
                    }}
                >
                    {sellLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : canSell ? 'Sell This Item' : notSellingLabel}
                </Button>
            </Box>
        </Paper>
    );
});

BuylistProductCard.propTypes = {
    product: PropTypes.object.isRequired,
    onAdd: PropTypes.func.isRequired,
    onProductClick: PropTypes.func.isRequired,
    storeName: PropTypes.string.isRequired,
    onImageClick: PropTypes.func.isRequired,
    buylistGameSettings: PropTypes.object,
    // This card's game buys slabs (store-config gradedEnabled). TKT-2026-107322
    gradedEnabled: PropTypes.bool,
};

// ─────────────────────────────────────────────────────────────────────────────
// RESULTS GRID
// ─────────────────────────────────────────────────────────────────────────────
// The individual cards are already memo()'d, but mapping the result set inline
// in the Dashboard body still rebuilt (and made React reconcile) one element per
// visible card on every keystroke. Holding the map in its own memo()'d component
// lets the whole grid bail out while the shopper is typing. TKT-2026-103916
const ResultsGrid = memo(({ results, kioskMode, storeName, showCardScans, stockDisplayMode, onAdd, onImageClick, onProductClick, buylistGameSettings, gradedCategoryIds }) => (
    <ProductGrid role='grid'>
        {results.map((p) => (
            kioskMode === 'buy' ? (
                <BuylistProductCard
                    key={p.productId}
                    product={p}
                    onAdd={onAdd}
                    storeName={storeName}
                    onImageClick={onImageClick}
                    onProductClick={onProductClick}
                    buylistGameSettings={buylistGameSettings}
                    gradedEnabled={isGradedCategory(gradedCategoryIds, p.categoryId)}
                />
            ) : (
                <ProductCard
                    key={p.productId}
                    product={p}
                    showCardScans={showCardScans}
                    stockDisplayMode={stockDisplayMode}
                    onAdd={onAdd}
                    onImageClick={onImageClick}
                    onProductClick={onProductClick}
                />
            )
        ))}
    </ProductGrid>
));
ResultsGrid.displayName = 'ResultsGrid';
ResultsGrid.propTypes = {
    results: PropTypes.array.isRequired,
    kioskMode: PropTypes.string,
    storeName: PropTypes.string.isRequired,
    showCardScans: PropTypes.bool,
    stockDisplayMode: PropTypes.oneOf(['exact', 'in_stock', 'hidden']),
    onAdd: PropTypes.func.isRequired,
    onImageClick: PropTypes.func.isRequired,
    onProductClick: PropTypes.func.isRequired,
    buylistGameSettings: PropTypes.object,
    gradedCategoryIds: PropTypes.arrayOf(PropTypes.number),
};

// ─────────────────────────────────────────────────────────────────────────────
// FILTER SECTION

// Collapsible group wrapper for registry-driven filter controls.
const FilterGroupSection = ({ group, theme, children }) => {
    const [open, setOpen] = useState(true);
    const valid = React.Children.toArray(children).filter(Boolean);
    if (!valid.length) return null;
    return (
        <Box>
            <Box
                role='button'
                tabIndex={0}
                onClick={() => setOpen((o) => !o)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    px: 0.5,
                    py: 1,
                    minHeight: 44,
                    userSelect: 'none',
                }}
            >
                <Typography variant='overline' sx={{ fontWeight: 700, letterSpacing: '0.08em', color: alpha(theme.palette.text.primary, 0.7) }}>
                    {group}
                </Typography>
                <ExpandMoreIcon
                    fontSize='small'
                    sx={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', opacity: 0.6 }}
                />
            </Box>
            <Collapse in={open} timeout='auto' unmountOnExit>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {valid}
                </Box>
            </Collapse>
        </Box>
    );
};
FilterGroupSection.propTypes = {
    group: PropTypes.string.isRequired,
    theme: PropTypes.object.isRequired,
    children: PropTypes.node,
};

// NOTE: this panel renders a dozen MUI Autocompletes plus every registry-driven
// control for the selected game, so it is one of the most expensive subtrees on
// the page. It takes `hasSearchTerm` (a boolean) rather than the search string
// itself: the panel only needs to know whether a search is active, and taking
// the raw term busted the memo() on every keystroke, re-rendering the whole
// panel between the keypress and the letter appearing. TKT-2026-103916
const FilterSection = memo(
    ({
        hasSearchTerm,
        categoriesList,
        groupsList,
        filterOptions,
        registry,
        filterSettings,
        selectedCategory,
        selectedGroup,
        filterValues,
        searchCardText,
        onCategoryChange,
        onGroupChange,
        onFilterChange,
        onSearchCardTextChange,
        onClearFilters,
        onClose,
        sortBy,
        onSortChange,
        buylistMode,
    }) => {
        const theme = useTheme();
        const { currencySymbol } = useKioskTheme();

        const hasActive = useMemo(
            () => Boolean(hasSearchTerm || selectedCategory || selectedGroup || Object.values(filterValues).some((v) => (Array.isArray(v) ? v.length > 0 : (v && typeof v === 'object' ? true : Boolean(v))))),
            [hasSearchTerm, selectedCategory, selectedGroup, filterValues],
        );

        const textFieldSx = useMemo(
            () => ({
                '& .MuiOutlinedInput-root': {
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: alpha(theme.palette.common.white, theme.palette.surfaceIsDark ? 0.12 : 0.92),
                    '& fieldset': {
                        borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.2 : 0.35),
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.35 : 0.6),
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.14)}`,
                    },
                },
            }),
            [theme],
        );

        const renderMultiField = (field, label) => {
            const options = createFilterArray(filterOptions, field) || [];
            if (!options.length) return null;
            return (
                <FilterBlock key={field}>
                    <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                        {label}
                    </Typography>
                    <Autocomplete
                        multiple
                        size='small'
                        options={options}
                        value={filterValues[field] || []}
                        onChange={(_, v) => onFilterChange(field, v)}
                        renderInput={(params) => <TextField {...params} placeholder='Any' size='small' variant='outlined' sx={textFieldSx} />}
                        renderTags={(tags, getTagProps) =>
                            tags.map((val, idx) => {
                                const tagProps = getTagProps({ index: idx });
                                return <Chip key={tagProps.key} label={val} size='small' variant='outlined' deleteIcon={<CloseIcon fontSize='small' />} data-tag-index={tagProps['data-tag-index']} tabIndex={tagProps.tabIndex} onDelete={tagProps.onDelete} />;
                            })
                        }
                    />
                </FilterBlock>
            );
        };

        // The legacy hand-built controls below each derive their option list from
        // `filterOptions`. Built inline they allocated a fresh array on every
        // render, which handed each Autocomplete a new `options` identity and
        // defeated its internal memoization. TKT-2026-103916
        const rarityOptions = useMemo(() => createFilterArray(filterOptions, 'rarities'), [filterOptions]);
        const colorOptions = useMemo(() => createFilterArray(filterOptions, 'colors'), [filterOptions]);
        const colorIdentityOptions = useMemo(() => createFilterArray(filterOptions, 'colorIdentities'), [filterOptions]);
        const printingOptions = useMemo(() => createFilterArray(filterOptions, 'printings'), [filterOptions]);

        // ── Registry-driven dynamic controls ─────────────────────────────────
        const categoryId = selectedCategory?.categoryId;
        const visibleEntries = useMemo(
            () => visibleFiltersFor(registry, categoryId, filterSettings, filterOptions),
            [registry, categoryId, filterSettings, filterOptions],
        );

        // Render a single registry entry by its control type.
        const renderRegistryEntry = (entry) => {
            const { key, control, label } = entry;
            if (control === 'range') {
                const bounds = rangeBounds(filterOptions, key);
                if (!bounds || bounds.min >= bounds.max) return null;
                const val = filterValues[key];
                const lo = val && Number.isFinite(Number(val.min)) ? Number(val.min) : bounds.min;
                const hi = val && Number.isFinite(Number(val.max)) ? Number(val.max) : bounds.max;
                const step = bounds.max - bounds.min > 50 ? 5 : 1;
                return (
                    <FilterBlock key={key}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <Typography variant='subtitle2' fontWeight='medium'>{label}</Typography>
                            <Typography variant='caption' color='text.secondary'>{lo} – {hi}</Typography>
                        </Box>
                        <Box sx={{ px: 1 }}>
                            <Slider
                                size='small'
                                value={[lo, hi]}
                                min={bounds.min}
                                max={bounds.max}
                                step={step}
                                valueLabelDisplay='auto'
                                onChange={(_, v) => onFilterChange(key, { min: v[0], max: v[1] })}
                                sx={{ mt: 1 }}
                            />
                        </Box>
                    </FilterBlock>
                );
            }
            if (control === 'toggle') {
                const checked = filterValues[key] === true;
                return (
                    <FilterBlock key={key}>
                        <FormControlLabel
                            control={<Switch checked={checked} onChange={(e) => onFilterChange(key, e.target.checked)} />}
                            label={<Typography variant='subtitle2' fontWeight='medium'>{label}</Typography>}
                            sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                            labelPlacement='start'
                        />
                    </FilterBlock>
                );
            }
            // chips / typeahead → multi Autocomplete
            const options = facetOptions(filterOptions, key);
            if (!options.length) return null;
            return (
                <FilterBlock key={key}>
                    <Typography variant='subtitle2' gutterBottom fontWeight='medium'>{label}</Typography>
                    <Autocomplete
                        multiple
                        size='small'
                        options={options}
                        value={Array.isArray(filterValues[key]) ? filterValues[key] : []}
                        onChange={(_, v) => onFilterChange(key, v)}
                        renderInput={(params) => <TextField {...params} placeholder='Any' size='small' variant='outlined' sx={textFieldSx} />}
                        renderTags={(tags, getTagProps) =>
                            tags.map((val, idx) => {
                                const tagProps = getTagProps({ index: idx });
                                return <Chip key={tagProps.key} label={val} size='small' variant='outlined' deleteIcon={<CloseIcon fontSize='small' />} data-tag-index={tagProps['data-tag-index']} tabIndex={tagProps.tabIndex} onDelete={tagProps.onDelete} />;
                            })
                        }
                    />
                </FilterBlock>
            );
        };

        // Group the visible entries by registry `group` (ordered), so the kiosk
        // shows collapsible sections.
        const groupedEntries = useMemo(() => {
            const map = new Map();
            for (const e of visibleEntries) {
                // Skip keys rendered by the legacy hand-built controls below to
                // avoid duplicate UI (they still emit their registry-key params).
                if (LEGACY_RENDERED_KEYS.has(e.key)) continue;
                if (!map.has(e.group)) map.set(e.group, []);
                map.get(e.group).push(e);
            }
            const order = [...FILTER_GROUP_ORDER, ...[...map.keys()].filter((g) => !FILTER_GROUP_ORDER.includes(g))];
            return order.filter((g) => map.has(g)).map((g) => [g, map.get(g)]);
        }, [visibleEntries]);

        // Which registry rarity entry (if any) is enabled for this game → drives
        // the legacy Rarity control's emitted key (always 'rarity').
        const rarityEnabled = useMemo(() => visibleEntries.some((e) => e.key === 'rarity'), [visibleEntries]);
        const colorEnabled = useMemo(() => visibleEntries.some((e) => e.key === 'color'), [visibleEntries]);
        const colorIdentityEnabled = useMemo(() => visibleEntries.some((e) => e.key === 'colorIdentity'), [visibleEntries]);
        // Price shows with or without a game selected. TKT-2026-105611
        const showPrice = useMemo(
            () => priceFilterVisible({ registry, filterSettings, categoryId, filterOptions, buylistMode }),
            [registry, filterSettings, categoryId, filterOptions, buylistMode],
        );
        const priceBounds = useMemo(() => rangeBounds(filterOptions, PRICE_FILTER_KEY), [filterOptions]);

        return (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                        <FilterListIcon /> Filters
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* TKT-2026-104396: this used to carry a CloseIcon, which made the
                            one X in the panel a filter-destroying button sitting exactly
                            where a close control belongs. Text only now. */}
                        {hasActive && (
                            <Button size='small' onClick={onClearFilters} sx={{ borderRadius: 12 }}>
                                Clear All
                            </Button>
                        )}
                        <IconButton size='small' onClick={onClose} aria-label='Close filters' sx={{ minWidth: 44, minHeight: 44 }}>
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Box>
                </Box>

                <Divider
                    sx={{
                        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.2 : 0.3),
                        opacity: 0.6,
                    }}
                />

                <FilterBlock>
                    <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                        Sort By
                    </Typography>
                    <StyledSelect
                        size='small'
                        value={sortBy}
                        onChange={(e) => onSortChange(e.target.value)}
                        fullWidth
                        variant='outlined'
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 14,
                                backgroundColor: alpha(theme.palette.common.white, theme.palette.surfaceIsDark ? 0.12 : 0.92),
                            },
                        }}
                    >
                        {SORT_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </StyledSelect>
                </FilterBlock>

                <FilterBlock>
                    <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                        Game
                    </Typography>
                    <Autocomplete
                        size='small'
                        options={categoriesList}
                        getOptionLabel={(o) => o.displayName || o.name || ''}
                        isOptionEqualToValue={(option, value) => option?.categoryId === value?.categoryId}
                        value={selectedCategory ?? null}
                        onChange={(_, v) => onCategoryChange(v)}
                        renderInput={(params) => <TextField {...params} placeholder='Select Game' size='small' variant='outlined' sx={textFieldSx} />}
                    />
                </FilterBlock>

                {groupsList.length > 0 && (
                    <FilterBlock>
                        <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                            Set
                        </Typography>
                        <Autocomplete
                            size='small'
                            options={groupsList}
                            getOptionLabel={(o) => (o.abbreviation ? `${o.abbreviation} (${o.name})` : o.name)}
                            isOptionEqualToValue={(option, value) => option?.groupId === value?.groupId}
                            value={selectedGroup ?? null}
                            onChange={(_, v) => onGroupChange(v)}
                            renderInput={(params) => <TextField {...params} placeholder='Select Set' size='small' variant='outlined' sx={textFieldSx} />}
                        />
                    </FilterBlock>
                )}

                {showPrice && (
                    <PriceRangeFilter
                        value={filterValues[PRICE_FILTER_KEY] || null}
                        bounds={priceBounds}
                        currencySymbol={currencySymbol}
                        onChange={onFilterChange}
                        textFieldSx={textFieldSx}
                    />
                )}

                {/* Universal Rarity — registry key `rarity`, shown when enabled.
                    When no game is selected yet, fall back to showing it (so the
                    catalog-wide rarity list still works). Now multi-select. */}
                {(rarityEnabled || !categoryId) && rarityOptions.length > 0 && (
                    <FilterBlock>
                        <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                            Rarity
                        </Typography>
                        <Autocomplete
                            multiple
                            size='small'
                            options={rarityOptions}
                            value={Array.isArray(filterValues.rarity) ? filterValues.rarity : []}
                            onChange={(_, v) => onFilterChange('rarity', v)}
                            renderInput={(params) => <TextField {...params} placeholder='Any' size='small' variant='outlined' sx={textFieldSx} />}
                            renderTags={(tags, getTagProps) =>
                                tags.map((val, idx) => {
                                    const tagProps = getTagProps({ index: idx });
                                    return <Chip key={tagProps.key} label={val} size='small' variant='outlined' deleteIcon={<CloseIcon fontSize='small' />} data-tag-index={tagProps['data-tag-index']} tabIndex={tagProps.tabIndex} onDelete={tagProps.onDelete} />;
                                })
                            }
                        />
                    </FilterBlock>
                )}

                {/* Color / Color Identity — registry keys, MTG. Now multi-select. */}
                {colorEnabled && colorOptions.length > 0 && (
                    <FilterBlock>
                        <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                            Color
                        </Typography>
                        <Autocomplete
                            multiple
                            size='small'
                            options={colorOptions}
                            value={Array.isArray(filterValues.color) ? filterValues.color : []}
                            onChange={(_, v) => onFilterChange('color', v)}
                            renderInput={(params) => <TextField {...params} placeholder='Any' size='small' variant='outlined' sx={textFieldSx} />}
                            renderTags={(tags, getTagProps) =>
                                tags.map((val, idx) => {
                                    const tagProps = getTagProps({ index: idx });
                                    return <Chip key={tagProps.key} label={val} size='small' variant='outlined' deleteIcon={<CloseIcon fontSize='small' />} data-tag-index={tagProps['data-tag-index']} tabIndex={tagProps.tabIndex} onDelete={tagProps.onDelete} />;
                                })
                            }
                        />
                    </FilterBlock>
                )}

                {colorIdentityEnabled && colorIdentityOptions.length > 0 && (
                    <FilterBlock>
                        <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                            Color Identity
                        </Typography>
                        <Autocomplete
                            multiple
                            size='small'
                            options={colorIdentityOptions}
                            value={Array.isArray(filterValues.colorIdentity) ? filterValues.colorIdentity : []}
                            onChange={(_, v) => onFilterChange('colorIdentity', v)}
                            renderInput={(params) => <TextField {...params} placeholder='Any' size='small' variant='outlined' sx={textFieldSx} />}
                            renderTags={(tags, getTagProps) =>
                                tags.map((val, idx) => {
                                    const tagProps = getTagProps({ index: idx });
                                    return <Chip key={tagProps.key} label={val} size='small' variant='outlined' deleteIcon={<CloseIcon fontSize='small' />} data-tag-index={tagProps['data-tag-index']} tabIndex={tagProps.tabIndex} onDelete={tagProps.onDelete} />;
                                })
                            }
                        />
                    </FilterBlock>
                )}

                {/* Printing & Frame Effects — legacy extras (not in the registry),
                    kept so existing behavior is preserved. */}
                <FilterBlock>
                    <Typography variant='subtitle2' gutterBottom fontWeight='medium'>
                        Printing
                    </Typography>
                    <Autocomplete
                        size='small'
                        options={printingOptions}
                        value={filterValues.printing ?? null}
                        onChange={(_, v) => onFilterChange('printing', v)}
                        renderInput={(params) => <TextField {...params} placeholder='Any' size='small' variant='outlined' sx={textFieldSx} />}
                        clearIcon={<CloseIcon fontSize='small' />}
                    />
                </FilterBlock>

                {renderMultiField('frame_effects', 'Frame Effects')}

                {/* Registry-driven dynamic filters, grouped & collapsible. */}
                {groupedEntries.map(([group, entries]) => (
                    <FilterGroupSection key={group} group={group} theme={theme}>
                        {entries.map((entry) => renderRegistryEntry(entry))}
                    </FilterGroupSection>
                ))}

                {/* Search card text toggle (also surfaced near the search bar). */}
                <FilterBlock>
                    <FormControlLabel
                        control={<Switch checked={!!searchCardText} onChange={(e) => onSearchCardTextChange(e.target.checked)} />}
                        label={
                            <Box>
                                <Typography variant='subtitle2' fontWeight='medium'>Search card text</Typography>
                                <Typography variant='caption' color='text.secondary'>Also match rules / ability text</Typography>
                            </Box>
                        }
                        sx={{ m: 0, justifyContent: 'space-between', width: '100%' }}
                        labelPlacement='start'
                    />
                </FilterBlock>
            </Box>
        );
    },
);
FilterSection.propTypes = {
    hasSearchTerm: PropTypes.bool,
    categoriesList: PropTypes.array.isRequired,
    groupsList: PropTypes.array.isRequired,
    filterOptions: PropTypes.object.isRequired,
    registry: PropTypes.array,
    filterSettings: PropTypes.object,
    selectedCategory: PropTypes.object,
    selectedGroup: PropTypes.object,
    filterValues: PropTypes.object.isRequired,
    buylistMode: PropTypes.bool,
    searchCardText: PropTypes.bool,
    onCategoryChange: PropTypes.func.isRequired,
    onGroupChange: PropTypes.func.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    onSearchCardTextChange: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    sortBy: PropTypes.string.isRequired,
    onSortChange: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
// CART ITEM & CART POPOVER
// ─────────────────────────────────────────────────────────────────────────────
const CartItem = memo(({ item, highlight, id, onRemove, onUpdateQty }) => {
    const theme = useTheme();
    const { formatMoney } = useKioskTheme();
    const displayPrice = item.mode === 'buy'
        ? (item.paymentType === 'Credit' ? (item.buylistCreditPrice || 0) : (item.buylistCashPrice || 0))
        : item.price;
    const isBuy = item.mode === 'buy';
    // The copy's own scan, else the art for its printing (TKT-2026-107421).
    const imageSrc = cartLineImage(item) || undefined;

    return (
        <Box
            id={id}
            sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: '12px',
                border: `1px solid ${alpha(
                    isBuy ? theme.palette.success.main : theme.palette.primary.main,
                    theme.palette.mode === 'light' ? 0.1 : 0.2
                )}`,
                background:
                    theme.palette.background.ownerCard
                    || (theme.palette.mode === 'light'
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(237,239,247,0.88) 100%)'
                        : 'linear-gradient(135deg, rgba(30,30,36,0.9) 0%, rgba(50,52,60,0.85) 100%)'),
                transition: 'all 0.3s ease',
                ...(highlight ? {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                } : {}),
                '&:hover': {
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
                '&:last-child': { mb: 0 },
            }}
        >
            {/* Top row: image + info + remove */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Avatar
                    variant='rounded'
                    src={imageSrc}
                    alt={item.cleanName}
                    sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: '8px',
                        border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.1 : 0.2)}`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                        '& img': { objectFit: 'contain', p: 0.25 },
                    }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant='body2'
                        fontWeight={600}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.3,
                            mb: 0.25,
                        }}
                    >
                        {item.cleanName}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' noWrap sx={{ display: 'block' }}>
                        {gradedLineLabel(item.variant) || <>{item.variant?.condAbbr} / {item.variant?.printing}</>}
                        {item.variant?.langAbbr && item.variant.langAbbr !== 'EN' ? ` / ${item.variant.langAbbr}` : ''}
                        {item.variant?.certNumber ? ` · Cert ${item.variant.certNumber}` : ''}
                    </Typography>
                </Box>
                <IconButton
                    size='small'
                    onClick={() => onRemove(
                        variantKey(item.variant),
                        item.mode,
                        isBuy ? item.productId : null,
                        isBuy ? item.paymentType : null
                    )}
                    aria-label={`Remove ${item.cleanName}`}
                    sx={{
                        color: 'text.disabled',
                        '&:hover': { color: 'error.main', backgroundColor: alpha(theme.palette.error.main, 0.08) },
                        flexShrink: 0,
                        mt: -0.5,
                        mr: -0.5,
                    }}
                >
                    <DeleteIcon fontSize='small' />
                </IconButton>
            </Box>

            {/* Bottom row: mode badge + qty stepper + price */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                <Chip
                    label={isBuy ? `Sell To Us · ${item.paymentType}` : 'Buy From Us'}
                    size='small'
                    color={isBuy ? 'success' : 'primary'}
                    variant='outlined'
                    sx={{
                        height: 22,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        borderRadius: '6px',
                    }}
                />

                {/* Qty stepper */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                }}>
                    <IconButton
                        size='small'
                        disabled={item.qty <= 1}
                        onClick={() => onUpdateQty(variantKey(item.variant), item.qty - 1, item.mode, isBuy ? item.productId : null, isBuy ? item.paymentType : null)}
                        sx={{ borderRadius: 0, px: 0.5, py: 0.25, '&:disabled': { opacity: 0.3 } }}
                    >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography sx={{ px: 1, fontSize: '0.8rem', fontWeight: 600, minWidth: 20, textAlign: 'center' }}>
                        {item.qty}
                    </Typography>
                    <IconButton
                        size='small'
                        // One cert number is one slab (TKT-2026-107322).
                        disabled={Boolean(item.variant?.certNumber)}
                        onClick={() => onUpdateQty(variantKey(item.variant), item.qty + 1, item.mode, isBuy ? item.productId : null, isBuy ? item.paymentType : null)}
                        sx={{ borderRadius: 0, px: 0.5, py: 0.25 }}
                    >
                        <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                {/* Price */}
                <Typography variant='body2' fontWeight={700} color={isBuy ? 'success.main' : 'primary.onSurface'}>
                    {formatMoney(displayPrice * item.qty)}
                </Typography>
            </Box>
        </Box>
    );
});
CartItem.propTypes = {
    item: PropTypes.object.isRequired,
    highlight: PropTypes.bool,
    id: PropTypes.string,
    onRemove: PropTypes.func.isRequired,
    onUpdateQty: PropTypes.func.isRequired,
};

const CartDrawer = memo(({ open, onClose, cart, totalPrice, navigate, storeName, highlightSku, isDesktop, isTablet, onRemove, onUpdateQty, buylistDisclaimer, salesTaxRate, taxOnNetTotal, taxInclusive }) => {
    const theme = useTheme();
    const { formatMoney } = useKioskTheme();
    // TKT-2026-105480. Adding opens this drawer, so a card the store's order
    // size limit kept out is explained here, where the shopper is looking.
    const { orderLimit } = useCart();
    const listRef = useRef(null);

    // Scroll to highlighted item when it changes
    useEffect(() => {
        if (!open || !highlightSku || !listRef.current) return;
        const el = document.getElementById(`cart-item-${highlightSku}`);
        if (el) {
            const listTop = listRef.current.getBoundingClientRect().top;
            const itemTop = el.getBoundingClientRect().top;
            listRef.current.scrollTo({
                top: listRef.current.scrollTop + (itemTop - listTop),
                behavior: 'smooth',
            });
        }
    }, [open, highlightSku]);

    // Compute sell / buy sub-totals
    const sellItems = cart.filter(i => i.mode !== 'buy');
    const buyItems = cart.filter(i => i.mode === 'buy');
    const sellTotal = sellItems.reduce((s, i) => s + i.price * i.qty, 0);
    const buyTotal = buyItems.reduce((s, i) => {
        const p = i.paymentType === 'Credit' ? (i.buylistCreditPrice || 0) : (i.buylistCashPrice || 0);
        return s + p * i.qty;
    }, 0);
    const hasBothModes = sellItems.length > 0 && buyItems.length > 0;

    return (
        <Drawer
            variant={isDesktop ? 'persistent' : 'temporary'}
            anchor='right'
            open={open}
            onClose={onClose}
            sx={{
                width: isTablet ? 300 : CONFIG.CART_DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': (t) => ({
                    width: isTablet ? 300 : CONFIG.CART_DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    border: 'none',
                    borderRadius: 0,
                    background:
                        t.palette.background.ownerCard
                        || (t.palette.mode === 'light'
                            ? 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(237,239,247,0.9) 100%)'
                            : 'linear-gradient(180deg, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.9) 100%)'),
                    backdropFilter: 'blur(18px)',
                    borderLeft: `1px solid ${alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.12 : 0.28)}`,
                    boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
                }),
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h6' fontWeight={700}>
                        Cart ({cart.length})
                    </Typography>
                    <IconButton size='small' onClick={onClose} aria-label='Close cart'>
                        <ClearIcon fontSize='small' />
                    </IconButton>
                </Box>

                {/* Scrollable item list */}
                <Box ref={listRef} sx={{ flexGrow: 1, overflowY: 'auto', px: 1, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-track': { backgroundColor: theme.palette.background.paper }, '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.secondary, 0.3), borderRadius: 3 } }}>
                    {cart.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ShoppingCartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                            <Typography color='text.secondary'>Your cart is empty</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ px: 0.5 }}>
                            {cart.map((item) => (
                                <CartItem
                                    // Keyed on the full variant identity: a slab and a raw copy
                                    // share a skuId, so a skuId-based key collided and React
                                    // reused one row for both. TKT-2026-105367.
                                    key={`${item.productId}-${variantKey(item.variant)}-${item.mode}-${item.paymentType || ''}`}
                                    id={`cart-item-${variantKey(item.variant)}-${item.mode}`}
                                    item={item}
                                    highlight={variantKey(item.variant) === highlightSku}
                                    onRemove={onRemove}
                                    onUpdateQty={onUpdateQty}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Sticky footer: totals + checkout */}
                {cart.length > 0 && (
                    <Box sx={(t) => ({ p: 2, borderTop: `1px solid ${alpha(t.palette.divider, 0.15)}`, background: alpha(t.palette.background.paper, 0.8), backdropFilter: 'blur(12px)' })}>
                        {hasBothModes && (
                            <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant='body2' color='text.secondary'>Sell Items ({sellItems.length})</Typography>
                                    <Typography variant='body2'>{formatMoney(sellTotal)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant='body2' color='success.main'>Buy Items ({buyItems.length})</Typography>
                                    <Typography variant='body2' color='success.main'>−{formatMoney(buyTotal)}</Typography>
                                </Box>
                                <Divider sx={{ mb: 1 }} />
                            </>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant='h6' fontWeight={700}>
                                {hasBothModes ? 'Net Subtotal' : 'Subtotal'}
                            </Typography>
                            <Typography variant='h6' color='primary.onSurface' fontWeight={700}>
                                {formatMoney(hasBothModes ? sellTotal - buyTotal : totalPrice)}
                            </Typography>
                        </Box>
                        {(() => {
                            const rate = parseFloat(salesTaxRate) || 0;
                            if (rate <= 0) {
                                return (
                                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', textAlign: 'right', mb: 1 }}>
                                        Plus applicable taxes
                                    </Typography>
                                );
                            }
                            const subtotal = hasBothModes ? sellTotal - buyTotal : totalPrice;
                            const taxBase = hasBothModes && !taxOnNetTotal ? sellTotal : subtotal;
                            const taxAmount = taxInclusive
                                ? Math.max(0, taxBase * rate / (100 + rate))
                                : Math.max(0, taxBase * (rate / 100));
                            const totalWithTax = taxInclusive ? subtotal : subtotal + taxAmount;
                            return (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant='body2' color='text.secondary'>Tax ({rate}%{taxInclusive ? ' incl.' : ''})</Typography>
                                        <Typography variant='body2' color='text.secondary'>{formatMoney(taxAmount)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant='h6' fontWeight={700}>Total</Typography>
                                        <Typography variant='h6' color='primary.onSurface' fontWeight={700}>{formatMoney(totalWithTax)}</Typography>
                                    </Box>
                                </>
                            );
                        })()}
                        {buyItems.length > 0 && (
                            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
                                {buylistDisclaimer || 'Payout may vary depending on item condition upon inspection. Applicable taxes may be deducted from payout.'}
                            </Typography>
                        )}
                        {orderLimit?.atLimit && (
                            <Alert severity={orderLimit.over ? 'warning' : 'info'} sx={{ mb: 1.5 }}>
                                {orderLimit.message}
                            </Alert>
                        )}
                        <Button
                            fullWidth
                            variant='contained'
                            size='large'
                            onClick={() => {
                                if (!isDesktop) onClose();
                                navigate(`/${storeName}/cart`);
                            }}
                        >
                            View Cart & Checkout
                        </Button>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
});
CartDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    cart: PropTypes.array.isRequired,
    totalPrice: PropTypes.number.isRequired,
    navigate: PropTypes.func.isRequired,
    storeName: PropTypes.string.isRequired,
    // A variantKey() now, not a raw skuId. TKT-2026-105367.
    highlightSku: PropTypes.string,
    isDesktop: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool,
    onRemove: PropTypes.func.isRequired,
    onUpdateQty: PropTypes.func.isRequired,
    buylistDisclaimer: PropTypes.string,
    salesTaxRate: PropTypes.number,
    taxInclusive: PropTypes.bool,
    taxOnNetTotal: PropTypes.bool,
};

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
    const { storeName } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const colorMode = useContext(ColorModeContext);
    const { setThemeOverrides, themeOverrides, setCurrency, formatMoney, filterSettings, setFilterSettings } = useKioskTheme();
    // eslint-disable-next-line no-unused-vars
    const { cart, addToCart, removeFromCart, updateQuantity, kioskMode, setKioskMode, buyCartItems, resetNonce } = useCart();

    const LS_KEY = `dashboardState-${storeName}`;

    // Integrations, Kiosk, Store Name. Its help text says "the name shown in
    // your kiosk header", but the header was reading the URL slug, so a store
    // whose slug and trading name differ saw the slug. Falls back to the slug,
    // which is what every store that has not set one keeps. TKT-2026-105169.
    const headerStoreName = themeOverrides?.storeName?.trim() || storeName;

    // ─── PIN AUTH STATE ─────────────────────────────────────────────────────
    const [pin, setPin] = useLocalStorageState(`${LS_KEY}-pin`, '');
    const [pinError, setPinError] = useState('');
    const [verifyingPin, setVerifyingPin] = useState(false);
    const [pinVerified, setPinVerified] = useLocalStorageState(`${LS_KEY}-pinVerified`, false);
    const [storeLogoUrl, setStoreLogoUrl] = useLocalStorageState(`${LS_KEY}-storeLogoUrl`, null);

    // ─── BUYLIST MODE STATE ─────────────────────────────────────────────────
    const [hasBuylistSubscription, setHasBuylistSubscription] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [buylistStoreId, setBuylistStoreId] = useState(null);
    const [buylistConfig, setBuylistConfig] = useState({});
    // The per-game block that holds the store's Sell To Us preselects
    // (condition, cash or credit). Fetched here from the start and, until
    // TKT-2026-106337, handed to nothing.
    const buylistGameSettings = buylistConfig?.gameSettings;
    const [buylistEnabledCategoryIds, setBuylistEnabledCategoryIds] = useState([]);
    // The games whose "Graded slabs" is on, as the buylist itself tells its
    // customers (store-config gradedEnabled). Sell To Us offers a Graded option
    // on exactly these. TKT-2026-107322
    const [gradedCategoryIds, setGradedCategoryIds] = useState([]);

    // ─── FULLSCREEN KIOSK LOCK ─────────────────────────────────────────────────
    // Whether this store wants the kiosk held in fullscreen at all. Owner setting,
    // Integrations then Kiosk, ON by default so every existing kiosk behaves
    // exactly as it did. Switching it off does not drop a kiosk that is already
    // fullscreen out of it, it only stops the kiosk putting it back, so an
    // operator who chose fullscreen themselves keeps it. TKT-2026-105170.
    const fullscreenLockEnabled = themeOverrides?.fullscreenLockEnabled !== false;
    // Ctrl+Shift+K, the staff maintenance release. Separate from the store
    // setting so the two cannot overwrite each other.
    //
    // The combination used to release the lock on the keystroke alone, with
    // nothing checked. The passcode guarded getting IN to the kiosk and guarded
    // nothing about getting back OUT to the desktop, so anyone standing at a
    // kiosk with a keyboard could drop it out of fullscreen and reach the
    // machine underneath, which on most shop counters is the same machine as the
    // till. Our own help pages publish the combination, so it was not obscure
    // either. It now opens a passcode prompt and releases only on the store's
    // own 6-digit code. TKT-2026-105376.
    const [fsUnlockedByStaff, setFsUnlockedByStaff] = useState(false);
    const fsLocked = fullscreenLockEnabled && !fsUnlockedByStaff;
    const [unlockPromptOpen, setUnlockPromptOpen] = useState(false);
    const [unlockPin, setUnlockPin] = useState('');
    const [unlockError, setUnlockError] = useState('');
    const [verifyingUnlock, setVerifyingUnlock] = useState(false);

    // Go fullscreen when the passcode is accepted, and only then.
    //
    // `pinVerified` is persisted, so on every later mount it is already true and
    // this used to re-request the screen. The cart is a SIBLING route
    // (/:storeName/cart), so leaving the cart by its back arrow mounts a fresh
    // Dashboard, and that tap is the user gesture the browser wants, which is
    // how a store owner browsing in an ordinary window got yanked into
    // fullscreen by the back arrow. Once fullscreen, the fullscreenchange guard
    // below is what keeps a locked kiosk there. TKT-2026-105170.
    const pinWasVerifiedRef = useRef(pinVerified);
    useEffect(() => {
        const justVerified = pinVerified && !pinWasVerifiedRef.current;
        pinWasVerifiedRef.current = pinVerified;
        if (justVerified && fsLocked) {
            const el = document.documentElement;
            const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
            if (req) req.call(el).catch(console.error);
        }
    }, [pinVerified, fsLocked]);

    // Block F11/Escape and re-enter fullscreen if they manage to exit
    useEffect(() => {
        const onKeyDown = (e) => {
            // if locked, swallow Esc/F11
            if (fsLocked && (e.key === 'Escape' || e.key === 'F11')) {
                e.preventDefault();
                e.stopPropagation();

                // re-request fullscreen immediately
                const el = document.documentElement;
                const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
                if (req) req.call(el).catch(console.error);
            }
            // Ctrl+Shift+K asks for the store passcode. It does NOT release the
            // lock by itself; handleUnlockSubmit does that, and only after the
            // server has accepted the code. TKT-2026-105376.
            if (fsLocked && e.ctrlKey && e.shiftKey && e.key.toUpperCase() === 'K') {
                e.preventDefault();
                setUnlockPin('');
                setUnlockError('');
                setUnlockPromptOpen(true);
            }
        };
        document.addEventListener('keydown', onKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
    }, [fsLocked]);

    // Releasing the lock, once the passcode is accepted. Kept out of the keydown
    // handler so the only path to `setFsUnlockedByStaff(true)` is a verified one.
    const handleUnlockSubmit = async () => {
        if (verifyingUnlock) return;
        setVerifyingUnlock(true);
        setUnlockError('');
        try {
            await verifyKioskUnlock(storeName, unlockPin);
            setUnlockPromptOpen(false);
            setUnlockPin('');
            setFsUnlockedByStaff(true);
            const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
            if (exit) exit.call(document).catch(console.error);
        } catch (err) {
            const status = err?.response?.status;
            const serverMsg = err?.response?.data?.message || err?.response?.data?.msg;
            if (status === 401) {
                setUnlockError(serverMsg || 'Invalid passcode.');
            } else {
                setUnlockError(serverMsg || 'Unable to check the passcode. Please try again.');
            }
        } finally {
            setVerifyingUnlock(false);
        }
    };

    // Disable right-click when locked
    useEffect(() => {
        const onContext = (e) => {
            if (fsLocked) e.preventDefault();
        };
        document.addEventListener('contextmenu', onContext);
        return () => document.removeEventListener('contextmenu', onContext);
    }, [fsLocked]);

    // Guard against any other fullscreen exits (e.g. via UI)
    // and re-enter if still locked
    useEffect(() => {
        const reenter = () => {
            if (!fsLocked) return;
            const noFs = !(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement);
            if (noFs) {
                const el = document.documentElement;
                const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
                if (req) req.call(el).catch(console.error);
            }
        };

        ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach((evt) => document.addEventListener(evt, reenter));
        return () => ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach((evt) => document.removeEventListener(evt, reenter));
    }, [fsLocked]);

    // ─── DASHBOARD STATE ─────────────────────────────────────────────────────
    // The search reads `searchTerm` undebounced: the whole query is debounced once, at
    // `debouncedFiltersKey`. It used to go in already debounced, and that first debounce
    // hid typing from `queryIsSettling`: the box's setPage(1) landed 300ms before its
    // query did, so for that window the OLD query was re-fetched and paged on, and the
    // new one started on whatever page that reached (2 or 3, never 1), appended under
    // the old results. It also made every typed search wait 600ms instead of 300ms.
    const [searchTerm, setSearchTerm] = useLocalStorageState(`${LS_KEY}-searchTerm`, '');
    const debouncedSuggestionTerm = useDebounce(searchTerm, CONFIG.SUGGEST_DEBOUNCE_MS);
    const [selectedCategory, setSelectedCategory] = useLocalStorageState(`${LS_KEY}-selectedCategory`, null);
    const [selectedGroup, setSelectedGroup] = useLocalStorageState(`${LS_KEY}-selectedGroup`, null);
    const [filterValues, setFilterValues] = useLocalStorageState(`${LS_KEY}-filterValues`, { ...DEFAULT_FILTER_VALUES });
    const [sortBy, setSortBy] = useLocalStorageState(`${LS_KEY}-sortBy`, 'name_asc');
    const [productType, setProductType] = useLocalStorageState(`${LS_KEY}-productType`, 'all');
    const [inStockOnly, setInStockOnly] = useLocalStorageState(`${LS_KEY}-inStockOnly`, true);
    // In Stock / All Items is a sell-mode filter. Buy mode searches the catalog, and
    // its chip is hidden there, so nothing in buy mode may read inStockOnly directly:
    // Clear All and the saved preference set it to true whatever the mode, which
    // turned Sell To Us into "cards we already stock". TKT-2026-107117
    const sellInStockOnly = kioskMode !== 'buy' && inStockOnly;
    const [showCardScans, setShowCardScans] = useLocalStorageState(`${LS_KEY}-showCardScans`, false);

    // Registry-driven advanced filters (TKT-2026-102569).
    const [searchCardText, setSearchCardText] = useLocalStorageState(`${LS_KEY}-searchCardText`, null);
    const [filterRegistry, setFilterRegistry] = useState(FILTER_REGISTRY_FALLBACK);
    const [gameRegistry, setGameRegistry] = useState(null); // per-game subset from /filters

    // One-time migration of any legacy-shaped persisted filterValues into the
    // registry-keyed model (old single-select strings + type/creatureType/legalStatuses).
    const migratedRef = useRef(false);
    useEffect(() => {
        if (migratedRef.current) return;
        migratedRef.current = true;
        const legacyShaped =
            'type' in filterValues || 'creatureType' in filterValues || 'legalStatuses' in filterValues ||
            !Array.isArray(filterValues.rarity) || !Array.isArray(filterValues.color) || !Array.isArray(filterValues.colorIdentity);
        if (legacyShaped) setFilterValues((fv) => migrateFilterValues(fv));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effective registry = per-game subset from /filters when available, else the
    // full registry (live-fetched or static fallback).
    const effectiveRegistry = useMemo(
        () => (Array.isArray(gameRegistry) && gameRegistry.length ? gameRegistry : filterRegistry),
        [gameRegistry, filterRegistry],
    );

    // searchCardText default: owner filterSettings.searchCardText ?? true.
    const effectiveSearchCardText = useMemo(
        () => (searchCardText == null ? (filterSettings?.searchCardText ?? true) : searchCardText),
        [searchCardText, filterSettings],
    );

    // Facet/range options for filters (declared before filterParams, which reads
    // ranges to decide which range bounds to emit). TKT-2026-102569 adds
    // `facets` + `ranges`.
    const [filterOptions, setFilterOptions] = useState({
        colors: [],
        colorIdentities: [],
        rarities: [],
        printings: [],
        frame_effects: [],
        legalStatuses: [],
        types: [],
        creatureTypes: [],
        facets: {},
        ranges: {},
    });

    const isSingle = productType === 'single' || productType === 'all';
    const isSealed = productType === 'sealed' || productType === 'all';
    const isCustom = productType === 'custom';

    const filterParams = useMemo(
        () => {
            // Registry-driven params: `key[]` arrays for chips/typeahead,
            // `<key>Min`/`<key>Max` for ranges, boolean toggles (foil/inkable).
            // Use the FULL registry here so every set key resolves its control
            // type regardless of which per-game subset /filters returned.
            const dynamic = buildDynamicParams(filterValues, filterRegistry, filterOptions);
            // A price is a price the store is selling at, so the backend treats
            // a price bound as in stock (an out-of-stock card has no price to
            // be inside it). Say the same here, or "All Items" plus a price
            // would wait for a search term the in-stock view never needs.
            // TKT-2026-105611
            const priceBounded = kioskMode !== 'buy' && (dynamic.priceMin !== undefined || dynamic.priceMax !== undefined);
            return {
                q: searchTerm,
                categoryId: selectedCategory?.categoryId,
                groupId: selectedGroup?.groupId,
                // In buy mode (Sell To Us), search all products; in sell mode (Buy From Us), respect inStockOnly
                inStock: sellInStockOnly || priceBounded,
                single: isSingle,
                sealed: isSealed,
                custom: isCustom,
                sortBy,
                // legacy extras (kept, not registry-driven)
                printing: filterValues.printing,
                frame_effects: filterValues.frame_effects,
                // registry advanced filters (rarity/color/colorIdentity/cardType/
                // creatureType/legality/ranges/foil/etc.)
                ...dynamic,
                // card-text search toggle → adds rules-text to `q` on the backend
                ...(searchTerm && effectiveSearchCardText ? { searchCardText: true } : {}),
                showCardScans,
                // In buy mode, restrict to enabled buylist categories if no specific category selected
                buylistCategoryIds: kioskMode === 'buy' && !selectedCategory?.categoryId && buylistEnabledCategoryIds.length > 0 ? buylistEnabledCategoryIds : undefined,
                // Flag to tell backend to skip inventory filtering and show all products
                buylistMode: kioskMode === 'buy',
            };
        },
        [searchTerm, selectedCategory, selectedGroup, sellInStockOnly, isSingle, isSealed, isCustom, sortBy, filterValues, filterRegistry, filterOptions, effectiveSearchCardText, showCardScans, kioskMode, buylistEnabledCategoryIds],
    );
    // Debounce a stable value, not the object. `filterParams` is rebuilt whenever
    // any of its many inputs changes identity, and several of those inputs are
    // fetch results that land after mount carrying data identical to what was
    // already on screen. Keying the search on the object meant each of those
    // re-ran the search, and a page-1 search flips `loading`, which swaps the
    // entire product grid for skeletons. That is the multiple flicker customers
    // see every time the kiosk returns to the home screen. Keying on the params
    // themselves means the search re-runs when the QUERY changes and not before.
    // TKT-2026-105056
    const filterParamsKey = useMemo(() => stableParamsKey(filterParams), [filterParams]);
    const debouncedFiltersKey = useDebounce(filterParamsKey, CONFIG.SEARCH_DEBOUNCE_MS);
    // The key is itself valid JSON, so the params come back out of it. That keeps
    // this derivation pure (no ref written during render) and guarantees a new
    // object appears here only when the query genuinely differs. Keys whose value
    // was `undefined` are dropped, which is what qs does with them anyway.
    const debouncedFilters = useMemo(() => JSON.parse(debouncedFiltersKey), [debouncedFiltersKey]);
    // True while the shopper has changed a filter and the debounce has not yet
    // caught up, so `debouncedFilters` still describes the PREVIOUS query.
    //
    // Every filter handler calls setPage(1), and `page` is a dependency of the
    // search effect, so the reset fired a search immediately, carrying the old
    // filters. The grid ended up holding page 1 of the query the shopper had
    // just left plus pages 2 and on of the one they had just chosen, and that
    // wasted page-1 search flips `loading`, which is one more full-screen
    // skeleton flash per pick. Waiting for the debounce is what the debounce is
    // for. TKT-2026-106404
    const queryIsSettling = filterParamsKey !== debouncedFiltersKey;

    const [page, setPage] = useState(1);
    // Set when a page comes back with no rows, which means this query has no
    // more to give regardless of what `total` claims. Cleared whenever a page-1
    // search starts, i.e. whenever the query itself changes. TKT-2026-106404
    const [exhausted, setExhausted] = useState(false);
    // The query the results on screen belong to (its debouncedFiltersKey), set when
    // that query's page 1 lands. Until then the grid still holds the PREVIOUS query's
    // rows, and their `results.length < total` says nothing about the new one. On the
    // render where a new search settles, before its page 1 has set `loading`, that
    // opened infinite scroll: a shopper who had scrolled to the bottom meanwhile had
    // the new query's page 2 claimed, which cancelled its page 1 and appended page 2
    // under the old results.
    const [resultsFor, setResultsFor] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);
    const [groupsList, setGroupsList] = useState([]);
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    // The text `suggestions` were fetched for. While the box holds exactly that, the
    // dropdown shows the server's answer unfiltered (utils/searchCorrection.js).
    const [suggestionsFor, setSuggestionsFor] = useState('');
    const [loadingSug, setLoadingSug] = useState(false);
    // A corrected search (`searchCorrection` on page 1): the state draws the line over
    // the results, the ref is what later pages are asked with. A ref, so that page 1
    // landing does not re-run the search effect. utils/searchCorrection.js
    const [searchCorrection, setSearchCorrection] = useState(null);
    const searchCorrectionRef = useRef(null);
    const [error, setError] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedProd, setSelectedProd] = useState(null);
    const [zoomImage, setZoomImage] = useState(null);
    const [zoomTitle, setZoomTitle] = useState('');
    // The back scan of the enlarged copy (Scans On only) and which side is up.
    // TKT-2026-105491
    const [zoomBackImage, setZoomBackImage] = useState(null);
    const [zoomSide, setZoomSide] = useState('front');
    const [drawerOpen, setDrawerOpen] = useState(isDesktop);
    const [showTop, setShowTop] = useState(false);

    const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
    const [highlightSku, setHighlightSku] = useState(null);

    // TKT-2026-103353: when the idle-reset timer fires, CartContext bumps resetNonce.
    // Return the Dashboard to a clean home state (close overlays, clear search/filters)
    // so it is ready for the next customer. Guarded so it never fires on initial mount.
    const idleResetFirstRun = useRef(true);
    useEffect(() => {
        if (idleResetFirstRun.current) {
            idleResetFirstRun.current = false;
            return;
        }
        setDetailOpen(false);
        setZoomImage(null);
        setZoomBackImage(null);
        setZoomSide('front');
        setCartDrawerOpen(false);
        setSearchTerm('');
        setSelectedCategory(null);
        setSelectedGroup(null);
        setFilterValues({ ...DEFAULT_FILTER_VALUES });
        setSortBy('name_asc');
        setProductType('all');
        try { window.scrollTo({ top: 0 }); } catch { /* noop */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetNonce]);

    const handleOpenImageZoom = useCallback((imageUrl, title, backImageUrl = null) => {
        if (!imageUrl) return;
        setZoomImage(imageUrl);
        setZoomTitle(title || 'Card image');
        setZoomBackImage(backImageUrl || null);
        setZoomSide('front');
    }, []);

    const handleCloseImageZoom = useCallback(() => {
        setZoomImage(null);
        setZoomTitle('');
        setZoomBackImage(null);
        setZoomSide('front');
    }, []);
    const handleOpenProduct = useCallback((product) => {
        setSelectedProd(product);
        setDetailOpen(true);
    }, []);
    const totalPrice = useMemo(() => cart.reduce((sum, i) => {
        if (i.mode === 'buy') {
            const price = i.paymentType === 'Credit'
                ? (i.buylistCreditPrice || 0)
                : (i.buylistCashPrice || 0);
            return sum + price * i.qty;
        }
        return sum + i.price * i.qty;
    }, 0), [cart]);
    const sortLabel = useMemo(() => SORT_OPTIONS.find((o) => o.value === sortBy)?.label || 'Custom', [sortBy]);
    const productTypeLabel = useMemo(() => {
        switch (productType) {
            case 'single':
                return 'Singles';
            case 'sealed':
                return 'Sealed Products';
            case 'custom':
                return 'Custom Inventory';
            default:
                return 'All Products';
        }
    }, [productType]);
    const activeFiltersCount = useMemo(() => {
        const baseCount = (searchTerm ? 1 : 0) + (selectedCategory ? 1 : 0) + (selectedGroup ? 1 : 0) + (productType !== 'all' ? 1 : 0) + (kioskMode !== 'buy' && !inStockOnly ? 1 : 0) + (showCardScans ? 1 : 0);
        const facetCount = Object.values(filterValues).reduce((acc, val) => {
            if (Array.isArray(val)) return acc + (val.length ? 1 : 0);
            return acc + (val ? 1 : 0);
        }, 0);
        return baseCount + facetCount;
    }, [filterValues, inStockOnly, kioskMode, productType, searchTerm, selectedCategory, selectedGroup, showCardScans]);
    const suggestionOptions = useMemo(() => createSuggestionOptions(suggestions), [suggestions]);
    const suggestionFilterOptions = useMemo(() => suggestionFilter(defaultSuggestionFilter, suggestionsFor), [suggestionsFor]);

    // ─── HOOK EFFECTS ────────────────────────────────────────────────────────
    // Same treatment as the search effect below. The `cancelled` guards are not
    // cosmetic: the bare `.catch(() => setSuggestions([]))` would now fire on
    // every cancellation and blank the dropdown that the replacing request is
    // about to refill, which reads as a flicker under fast typing.
    // TKT-2026-103916
    useEffect(() => {
        const suggestionTerm = debouncedSuggestionTerm.trim();
        if (suggestionTerm.length < 2) {
            setSuggestions([]);
            setSuggestionsFor('');
            setLoadingSug(false);
            return;
        }
        const controller = new AbortController();
        let cancelled = false;
        setLoadingSug(true);
        suggestProducts(
            storeName,
            {
                q: suggestionTerm,
                ...(selectedCategory && { categoryId: selectedCategory.categoryId }),
                ...(selectedGroup && { groupId: selectedGroup.groupId }),
                // Sell To Us suggests from the game's catalog, not the shelf. TKT-2026-107117
                ...(kioskMode === 'buy' && { buylistMode: true }),
                limit: CONFIG.MAX_SUGGESTIONS,
            },
            { signal: controller.signal },
        )
            .then((r) => {
                if (cancelled) return;
                setSuggestions(r.data.suggestions || []);
                setSuggestionsFor(suggestionTerm);
            })
            .catch((err) => { if (!cancelled && !isCanceledError(err)) setSuggestions([]); })
            .finally(() => { if (!cancelled) setLoadingSug(false); });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [debouncedSuggestionTerm, selectedCategory, selectedGroup, storeName, kioskMode]);

    useEffect(() => {
        const params = kioskMode === 'buy'
            ? { buylistMode: true, ...(buylistEnabledCategoryIds.length > 0 && { buylistCategoryIds: buylistEnabledCategoryIds.join(',') }) }
            : undefined;
        fetchCategories(storeName, params)
            .then((r) => setCategoriesList(r.data.categories))
            .catch(() => setError('Failed to load categories'));
    }, [storeName, kioskMode, buylistEnabledCategoryIds]);

    // Fetch the filter registry once (TKT-2026-102569). Falls back to the static
    // mirror if the endpoint is unavailable (e.g. before PR #80 merges).
    useEffect(() => {
        let cancelled = false;
        fetchFilterRegistry()
            .then((r) => {
                const reg = r?.data?.registry;
                // Same registry as the static fallback on most loads. Writing it
                // anyway re-ran both the /filters lookup and the product search.
                // TKT-2026-105056
                if (!cancelled && Array.isArray(reg) && reg.length) {
                    setFilterRegistry((prev) => keepIfEqual(prev, reg));
                }
            })
            .catch(() => { /* keep the static fallback registry */ });
        return () => { cancelled = true; };
    }, []);

    // Filter categories for buy mode - only show games that are enabled in buylist config
    // (server already filters in buy mode, but keep client filter as safety net)
    const filteredCategoriesList = useMemo(() => {
        if (kioskMode !== 'buy' || buylistEnabledCategoryIds.length === 0) {
            return categoriesList;
        }
        return categoriesList.filter(cat => buylistEnabledCategoryIds.includes(cat.categoryId));
    }, [categoriesList, kioskMode, buylistEnabledCategoryIds]);

    // ─── FETCH BUYLIST CONFIG ─────────────────────────────────────────────────
    useEffect(() => {
        const checkBuylistAccess = async () => {
            try {
                const response = await fetchBuylistConfig(storeName);
                setHasBuylistSubscription(response.data.hasBuylist);
                setBuylistStoreId(response.data.storeId);
                setBuylistConfig(response.data.config || {});
                // Keep the existing array when the ids are unchanged. This value
                // feeds the search params, so a fresh `[]` for an unchanged empty
                // list used to re-run the whole product search. TKT-2026-105056
                setBuylistEnabledCategoryIds((prev) =>
                    keepIfEqual(prev, response.data.enabledCategoryIds || []));
                setGradedCategoryIds((prev) =>
                    keepIfEqual(prev, Array.isArray(response.data.gradedCategoryIds) ? response.data.gradedCategoryIds : []));
            } catch (error) {
                // Invalid store slug in the URL is an expected "no access" state,
                // not a bug. The backend returns 404 with a "Store ... not found"
                // message; surface the same not-found UI without logging.
                const status = error?.response?.status;
                const serverMsg = error?.response?.data?.msg || '';
                const isUnknownStore =
                    status === 404 && /Store .* not found/i.test(serverMsg);
                if (!isUnknownStore) {
                    console.error('Failed to fetch buylist config:', error);
                }
                setHasBuylistSubscription(false);
                setBuylistEnabledCategoryIds((prev) => keepIfEqual(prev, []));
                setGradedCategoryIds((prev) => keepIfEqual(prev, []));
            }
        };
        checkBuylistAccess();
    }, [storeName]);

    useEffect(() => {
        async function load() {
            if (!selectedCategory) {
                setGroupsList([]);
                if (selectedGroup) {
                    setSelectedGroup(null);
                }
            } else {
                const groupParams = { categoryId: selectedCategory.categoryId };
                if (kioskMode === 'buy') groupParams.buylistMode = true;
                const {
                    data: { groups },
                } = await fetchGroups(storeName, groupParams);
                setGroupsList(groups);
                if (selectedGroup?.groupId) {
                    const match = groups.find((g) => g.groupId === selectedGroup.groupId);
                    if (!match) {
                        setSelectedGroup(null);
                    }
                }
            }
            try {
                // Forward the currently-selected registry filters so the returned
                // facets/ranges narrow contextually (same param contract as search).
                const dynamic = buildDynamicParams(filterValues, filterRegistry, filterOptions);
                const fRes = await fetchFilters(storeName, {
                    inStock: sellInStockOnly,
                    // Sell To Us offers filter values from the game's catalog. TKT-2026-107117
                    ...(kioskMode === 'buy' && { buylistMode: true }),
                    single: isSingle,
                    sealed: isSealed,
                    custom: isCustom,
                    ...(selectedCategory && { categoryId: selectedCategory.categoryId }),
                    ...(selectedGroup && { groupId: selectedGroup.groupId }),
                    ...(filterValues.printing && { printing: filterValues.printing }),
                    ...(filterValues.frame_effects?.length && { frame_effects: filterValues.frame_effects }),
                    ...dynamic,
                    showCardScans,
                });
                // Both of these feed the search params, so they only get written
                // when the facets actually differ. TKT-2026-105056
                setFilterOptions((prev) => keepIfEqual(prev, {
                    colors: fRes.data.colors || [],
                    colorIdentities: fRes.data.colorIdentities || [],
                    rarities: fRes.data.rarities || [],
                    printings: fRes.data.printings || [],
                    frame_effects: fRes.data.frame_effects || [],
                    legalStatuses: fRes.data.legalStatuses || [],
                    types: fRes.data.types || [],
                    creatureTypes: fRes.data.creatureTypes || [],
                    facets: fRes.data.facets || {},
                    ranges: fRes.data.ranges || {},
                }));
                // Capture the per-game registry subset when the backend returns it.
                if (Array.isArray(fRes.data.registry)) {
                    setGameRegistry((prev) => keepIfEqual(prev, fRes.data.registry));
                }
            } catch {
                setError('Failed to load filters');
            }
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeName, selectedCategory, selectedGroup, setSelectedGroup, filterValues, sellInStockOnly, isSingle, isSealed, isCustom, showCardScans, filterRegistry, kioskMode]);

    // The third argument here used to be dropped on the floor: searchProducts
    // took only (storeName, params), so the AbortSignal never reached axios and
    // abort() was a no-op. Two consequences, both visible to the customer while
    // typing: every superseded /search ran to completion against the store's
    // whole catalog, and its response could still land last and overwrite the
    // newer one (or, on the `first === false` branch, append a stale page).
    //
    // One controller per effect run, aborted from the CLEANUP. React runs run
    // N's cleanup before run N+1's body, so `cancelled` is a correct ordering
    // guard. Aborting in cleanup rather than at the top of the next body also
    // covers the two paths the old placement missed: the cleared-search early
    // return below (which returned before ever reaching the abort, so a stale
    // response repopulated a grid the user had just cleared) and unmount, which
    // this effect had no handling for at all. TKT-2026-103916
    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;

        const doSearch = async () => {
            // The query the shopper is now asking for is not the one in
            // `debouncedFilters` yet. Fetching here would fetch the old one.
            // Leave the grid exactly as it is until the debounce settles; the
            // effect re-runs the moment it does. TKT-2026-106404
            if (queryIsSettling) return;

            // In buy mode (Sell To Us), require a search term before querying
            // Also in sell mode (Buy From Us) with inStock disabled, require a search term
            const requiresSearchTerm =
                kioskMode === 'buy' ||
                (kioskMode === 'sell' && debouncedFilters.inStock === false);

            if (requiresSearchTerm && !debouncedFilters.q?.trim()) {
                searchCorrectionRef.current = null;
                setSearchCorrection(null);
                setResults([]);
                setTotal(0);
                setLoading(false);
                setLoadingMore(false);
                return;
            }

            const first = page === 1;
            if (first) {
                setExhausted(false);
                searchCorrectionRef.current = null;
                setSearchCorrection(null);
            }
            first ? setLoading(true) : setLoadingMore(true);
            // A later page of a corrected search is asked with the corrected words:
            // the typed ones match nothing, so their page 2 is empty.
            const q = searchTermForPage(debouncedFilters.q, page, searchCorrectionRef.current);
            try {
                const { data } = await searchProducts(
                    storeName,
                    { ...debouncedFilters, ...(q !== debouncedFilters.q && { q }), page, size: CONFIG.PAGE_SIZE },
                    { signal: controller.signal },
                );
                if (cancelled) return;
                const rows = data.results || [];
                if (first) {
                    const correction = readSearchCorrection(data);
                    searchCorrectionRef.current = correction;
                    setSearchCorrection(correction);
                    setResultsFor(debouncedFiltersKey);
                }
                setTotal(data.total || 0);
                setResults((prev) => (first ? rows : [...prev, ...rows]));
                // A page that came back with nothing in it IS the end of the
                // results, whatever the count says. Paging stops on
                // `results.length < total`, which trusts a number the grid does
                // not control, so any disagreement between the two turns into an
                // unbounded walk through the catalog: on the reporting store a
                // printing pick asked for between 59 and 142 consecutive pages,
                // every one of them empty. The cause of that disagreement is
                // fixed on the backend (the printing now selects products, so
                // the count and the rows describe the same query); this is the
                // loop terminating on its own evidence instead of on someone
                // else's arithmetic. TKT-2026-106404
                if (rows.length === 0) setExhausted(true);
                setError(null);
            } catch (err) {
                // A superseded request is not a failure. Without this guard the
                // persistent red banner would appear on every keystroke, since
                // axios rejects a cancelled request with CanceledError and the
                // old `err.name !== 'AbortError'` test was true for it.
                if (cancelled || isCanceledError(err)) return;
                setError('Failed to load products');
            } finally {
                // Guarded for the same reason: a cancelled run's finally resolves
                // a microtask after abort(), long before the replacing request
                // lands. Clearing the flags there would tear the skeleton down
                // mid-search and flash "No products found" between keystrokes.
                if (!cancelled) {
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        };
        doSearch();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [storeName, page, debouncedFilters, debouncedFiltersKey, kioskMode, queryIsSettling]);

    // Never paginate a query the shopper has already moved off: page 2 of the
    // old filters would be appended under page 1 of the new ones. TKT-2026-106404
    // Nor a query whose page 1 has not landed: the results on screen are still the
    // previous query's (`resultsFor`).
    const attachLoadMore = useInfiniteScroll(
        () => setPage((p) => p + 1),
        results.length < total && !exhausted && !loading && !loadingMore && !queryIsSettling && resultsFor === debouncedFiltersKey,
    );
    useEffect(() => {
        if (isMobile) setDrawerOpen(false);
    }, [isMobile]);

    const handleAddToCart = useCallback(
        (prod, variant, buylistOptions = null) => {
            if (!variant) return;
            // The art for this copy's own printing and language, not the
            // productId's one render (the 1st Edition photo of a WOTC-era Pokemon
            // card). A buy-mode line is a printing the shopper picked, so it is
            // resolved here by the same chain. TKT-2026-107421
            const catalogArt = variantCatalogArt(prod, variant);
            const variantImage = variantScanImage(variant) || catalogArt;
            const priceValue = parseFloat(variant.price ?? variant.midPrice ?? variant.marketPrice ?? variant.lowPrice ?? getPrice(prod));

            // Build cart item with optional buylist data
            const cartItem = {
                productId: prod.productId,
                cleanName: prod.cleanName,
                cdnImage: variantImage,
                // The card's own catalog image, kept alongside the resolved one so
                // that if the cart optimiser later moves this line to a copy with
                // no scan of its own, it has something to fall back to instead of
                // showing the previous copy's scan. TKT-2026-105225
                catalogImage: catalogArt,
                price: Number.isFinite(priceValue) ? priceValue : parseFloat(getPrice(prod)),
                qty: 1,
                variant,
                mode: buylistOptions?.mode || kioskMode,
                groupName: prod.groupName,
                categoryId: prod.categoryId,
                categoryName: prod.categoryName || '',
                number: prod.number || prod.collectorNumber || '',
                rarity: prod.rarity || '',
                setName: prod.setName || prod.groupName || '',
            };

            // Add buylist-specific data if in buy mode
            if (buylistOptions?.mode === 'buy' || (!buylistOptions && kioskMode === 'buy')) {
                cartItem.paymentType = buylistOptions?.paymentType || 'Cash';
                cartItem.buylistCashPrice = buylistOptions?.buylistCashPrice || 0;
                cartItem.buylistCreditPrice = buylistOptions?.buylistCreditPrice || 0;
                cartItem.awaitingStorePrice = buylistOptions?.awaitingStorePrice === true;
                // The store's quantity ladder, so several copies are priced the
                // way the buylist books them (TKT-2026-107051).
                cartItem.buylistLadder = buylistOptions?.buylistLadder || null;
                // Reference prices from buylist API for POS display
                cartItem.marketPrice = buylistOptions?.marketPrice || 0;
                cartItem.lowPrice = buylistOptions?.lowPrice || 0;
                cartItem.midPrice = buylistOptions?.midPrice || 0;
                cartItem.directLowPrice = buylistOptions?.directLowPrice || 0;
                cartItem.highPrice = buylistOptions?.highPrice || 0;
                cartItem.chosenPrice = buylistOptions?.chosenPrice || 0;
                cartItem.chosenPriceType = buylistOptions?.chosenPriceType || '';
            }

            addToCart(cartItem);
            setCartDrawerOpen(true);
            setHighlightSku(variantKey(variant));
        },
        [addToCart, kioskMode],
    );
    const handleScrollTop = useCallback(() => {
        setShowTop(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setShowTop(false), 300);
    }, []);

    // ─── FILTER HANDLERS (registry-driven) ───────────────────────────────────
    // A single setter for any registry key (array | {min,max} | boolean) or the
    // legacy `printing` (string|null).
    const handleFilterChange = useCallback((key, val) => {
        setFilterValues((fv) => ({ ...fv, [key]: val ?? null }));
        setPage(1);
    }, [setFilterValues]);

    // Reset everything (extends the original Clear All to the registry model).
    const handleClearFilters = useCallback(() => {
        setSearchTerm('');
        setSelectedCategory(null);
        setSelectedGroup(null);
        setFilterValues({ ...DEFAULT_FILTER_VALUES });
        setSortBy('name_asc');
        setProductType('all');
        setInStockOnly(true);
        setShowCardScans(false);
        setSearchCardText(null);
        setPage(1);
    }, [setSearchTerm, setSelectedCategory, setSelectedGroup, setFilterValues, setSortBy, setProductType, setInStockOnly, setShowCardScans, setSearchCardText]);

    const handleSearchCardTextChange = useCallback((checked) => {
        setSearchCardText(!!checked);
        setPage(1);
    }, [setSearchCardText]);

    // Stable identities for the FilterSection / CartDrawer callbacks. Declared
    // inline they were rebuilt on every Dashboard render, so both memo()'d
    // subtrees re-rendered on every keystroke. TKT-2026-103916
    const handleGroupChange = useCallback((grp) => {
        setSelectedGroup(grp ?? null);
        setPage(1);
    }, [setSelectedGroup]);

    const handleSortChange = useCallback((val) => {
        setSortBy(val);
        setPage(1);
    }, [setSortBy]);

    const handleCloseCartDrawer = useCallback(() => setCartDrawerOpen(false), []);
    const handleCloseFilterDrawer = useCallback(() => setDrawerOpen(false), []);

    // The filter drawer's sx is a large nested style object; rebuilt inline it
    // was re-serialized by emotion on every render. TKT-2026-103916
    // TKT-2026-104396: on a phone the paper was a hard 350px, leaving only
    // 10px of tappable backdrop at 360px (and that sliver sits inside the OS
    // back-gesture strip). Clamp below lg so a real dismiss target always
    // remains. The docked lg width must stay exactly CONFIG.DRAWER_WIDTH,
    // because the content Box offsets itself by that same constant.
    const filterDrawerPaperWidth = isDesktop
        ? CONFIG.DRAWER_WIDTH
        : (isTablet ? 280 : `min(${CONFIG.DRAWER_WIDTH}px, 85vw)`);

    const filterDrawerSx = useMemo(() => ({
        width: filterDrawerPaperWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': (t) => ({
            width: filterDrawerPaperWidth,
            boxSizing: 'border-box',
            border: 'none',
            padding: t.spacing(isTablet ? 2 : 3),
            borderRadius: 0,
            background:
                t.palette.background.ownerCard
                || (t.palette.mode === 'light'
                    ? 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(237,239,247,0.9) 100%)'
                    : 'linear-gradient(180deg, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.9) 100%)'),
            backdropFilter: 'blur(18px)',
            borderRight: `1px solid ${alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.12 : 0.28)}`,
            boxShadow: '0 32px 80px rgba(0,0,0,0.22)',
        }),
    }), [isTablet, filterDrawerPaperWidth]);

    // Switching game: drop registry selections that no longer apply to the new
    // game so stale chips don't linger (keeps the applied-filters bar honest).
    const handleCategoryChange = useCallback((cat) => {
        const nextCatId = cat?.categoryId;
        setSelectedCategory(cat ?? null);
        setFilterValues((fv) => {
            // Price applies to every game, so it survives clearing the game too.
            // TKT-2026-105611
            if (!nextCatId) return { ...DEFAULT_FILTER_VALUES, printing: fv.printing, frame_effects: fv.frame_effects, ...(fv[PRICE_FILTER_KEY] && { [PRICE_FILTER_KEY]: fv[PRICE_FILTER_KEY] }) };
            const applicable = new Set(
                filterRegistry
                    .filter((e) => appliesToGame(e, nextCatId))
                    .map((e) => e.key),
            );
            const next = { ...DEFAULT_FILTER_VALUES, printing: fv.printing, frame_effects: fv.frame_effects };
            for (const [k, v] of Object.entries(fv)) {
                if (k === 'printing' || k === 'frame_effects') continue;
                if (applicable.has(k)) next[k] = v;
            }
            return next;
        });
        setPage(1);
    }, [setSelectedCategory, setFilterValues, filterRegistry]);

    // Build the applied-filters chip list (each removable). Covers registry keys
    // + legacy extras + search-scope toggles.
    const appliedFilterChips = useMemo(() => {
        const chips = [];
        const labelFor = (key) => filterRegistry.find((e) => e.key === key)?.label || key;
        for (const [key, val] of Object.entries(filterValues)) {
            if (key === 'frame_effects') {
                (Array.isArray(val) ? val : []).forEach((v) =>
                    chips.push({ id: `frame_effects:${v}`, label: `Frame: ${v}`, onDelete: () => handleFilterChange('frame_effects', filterValues.frame_effects.filter((x) => x !== v)) }));
                continue;
            }
            if (key === 'printing') {
                if (val) chips.push({ id: 'printing', label: `Printing: ${val}`, onDelete: () => handleFilterChange('printing', null) });
                continue;
            }
            if (Array.isArray(val)) {
                val.forEach((v) =>
                    chips.push({ id: `${key}:${v}`, label: `${labelFor(key)}: ${v}`, onDelete: () => handleFilterChange(key, val.filter((x) => x !== v)) }));
            } else if (val && typeof val === 'object' && ('min' in val || 'max' in val)) {
                // The store's own prices do not apply in buy mode, and the
                // backend ignores the bound there, so do not claim it is applied.
                if (key === PRICE_FILTER_KEY && kioskMode === 'buy') continue;
                const entry = filterRegistry.find((e) => e.key === key) || { key, control: 'range' };
                const text = formatRangeValue(entry, val, formatMoney);
                if (text) chips.push({ id: key, label: `${labelFor(key)}: ${text}`, onDelete: () => handleFilterChange(key, null) });
            } else if (val === true) {
                chips.push({ id: key, label: labelFor(key), onDelete: () => handleFilterChange(key, false) });
            }
        }
        return chips;
    }, [filterValues, filterRegistry, handleFilterChange, kioskMode, formatMoney]);

    // Apply the store's display config (theme/logo/tax/currency) returned by either
    // the PIN-verify endpoint or the public theme endpoint. Single source of truth so
    // the unlock path and the on-load refresh path stay identical. TKT-2026-102403
    const applyStoreConfig = useCallback((data) => {
        if (!data) return;
        setStoreLogoUrl(data.storeLogoUrl || null);
        setThemeOverrides({
            ...(data.kioskTheme || {}),
            salesTaxRate: data.salesTaxRate || 0,
            taxOnNetTotal: data.taxOnNetTotal === true,
            taxInclusive: data.taxInclusive === true,
            showCardLocations: data.showCardLocations === true,
            // TKT-2026-103353
            stockDisplayMode: data.stockDisplayMode || 'exact',
            printReceiptEnabled: data.printReceiptEnabled === true,
            qzAutoPrintReceipt: data.qzAutoPrintReceipt === true,
            idleResetEnabled: data.idleResetEnabled === true,
            idleResetTimeoutMinutes: Number(data.idleResetTimeoutMinutes) || 5,
            // TKT-2026-105056
            onScreenKeyboardEnabled: data.onScreenKeyboardEnabled === true,
            // TKT-2026-105225. Absent (an older kiosk-api) reads as off, so the
            // cart keeps picking copies exactly as it always has.
            cartOptimization: data.cartOptimization || null,
            // TKT-2026-105170. Read as "not switched off" rather than "=== true",
            // so a kiosk still running against an older backend, or one whose
            // cached overrides predate this setting, keeps the lock it has always
            // had instead of silently losing it.
            fullscreenLockEnabled: data.fullscreenLockEnabled !== false,
            // TKT-2026-105480. The store's order size limit. Absent (an older
            // kiosk-api) reads as no limit, which is also every store's default.
            maxOrderLines: data.maxOrderLines ?? null,
            maxOrderUnits: data.maxOrderUnits ?? null,
            largeOrderUrl: data.largeOrderUrl || '',
        });
        // The code, not just its symbol: the code decides the decimals (none for
        // JPY/KRW/CLP/ISK) as well as the sign. TKT-2026-106987
        // The sign from sortswift-api's one list; null or absent draws it from the
        // code. TKT-2026-107434
        setCurrency(data.currency, data.currencySymbol);
        // Owner filter enable/disable map (TKT-2026-102569). Both theme + verify-pin
        // return `filterSettings`; persist it so the kiosk reflects owner choices.
        if (data.filterSettings && typeof data.filterSettings === 'object') {
            setFilterSettings(data.filterSettings);
        }
    }, [setStoreLogoUrl, setThemeOverrides, setCurrency, setFilterSettings]);

    // Refresh the theme/logo/tax/currency on every kiosk load (and store change).
    // The PIN-verified flag is cached in localStorage, so a previously-unlocked kiosk
    // skips the PIN dialog and never re-runs handlePinSubmit; without this the saved
    // theme would never be picked up after the first unlock. TKT-2026-102403
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await fetchKioskTheme(storeName);
                if (!cancelled) applyStoreConfig(data);
            } catch {
                /* keep whatever theme is cached; default theme renders otherwise */
            }
        })();
        return () => { cancelled = true; };
    }, [storeName, applyStoreConfig]);

    const handlePinSubmit = async () => {
        // 1) Validate PIN
        if (!/^\d{6}$/.test(pin)) {
            setPinError('Passcode must be exactly 6 digits.');
            return;
        }
        setVerifyingPin(true);
        setPinError('');

        // 2) Get geo-IP
        let coords = null;
        try {
            const resp = await fetch('https://ipapi.co/json/');
            const json = await resp.json();
            if (json.latitude && json.longitude) {
                coords = { latitude: json.latitude, longitude: json.longitude };
            }
        } catch {
            /* ignore */
        }

        if (!coords) {
            setVerifyingPin(false);
            setPinError(
                'Unable to determine your location via IP lookup. Please ensure:\n' +
                '• You are online and can reach external services\n' +
                '• No VPNs or proxies are blocking requests\n' +
                '• If possible, enable browser location services for greater accuracy',
            );
            return;
        }

        // 3) Collect extra identifying data
        const identifyingData = {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            screen: {
                width: window.screen.width,
                height: window.screen.height,
                pixelRatio: window.devicePixelRatio,
            },
            connection: navigator.connection
                ? {
                    effectiveType: navigator.connection.effectiveType,
                    downlink: navigator.connection.downlink,
                    rtt: navigator.connection.rtt,
                }
                : null,
            timestamp: new Date().toISOString(),
        };

        // 4) Build payload
        const payload = {
            pin,
            location: coords,
            identifyingData,
        };

        // 5) Call your API
        try {
            const { data } = await verifyKioskPin(storeName, payload);
            applyStoreConfig(data);
            setPinVerified(true);
        } catch (err) {
            const status = err.response?.status;
            // resolveStore returns its 404 under `msg`, verify-pin under `message` —
            // read both so the real reason is never lost.
            const serverMsg = err.response?.data?.message || err.response?.data?.msg;
            if (status === 404) {
                // The store could not be resolved from the URL slug. This is NOT a
                // passcode problem; showing "Invalid passcode" here sent first-time
                // setup tickets chasing a nonexistent passcode bug (TKT-2026-102525).
                setPinError(
                    'Kiosk not found for this web address. Open SortSwift, go to Integrations then Kiosk Settings, set a Kiosk Slug, and make sure this page uses that exact slug (kiosk.sortswift.com/your-slug).',
                );
            } else if (status === 401) {
                setPinError(serverMsg || 'Invalid passcode.');
            } else {
                setPinError(serverMsg || 'Unable to verify passcode. Please try again.');
            }
        } finally {
            setVerifyingPin(false);
        }
    };

    if (!pinVerified) {
        return (
            <Dialog open fullWidth maxWidth='sm'>
                <DialogTitle>Enter Kiosk Passcode (IN STORE USE ONLY)</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin='dense'
                        label='6-digit passcode'
                        value={pin}
                        onChange={(e) => setPin(e.target.value)}
                        error={!!pinError}
                        helperText={pinError || 'This Kiosk is only meant to be logged in on Store systems, it is not for remote customer use.'}
                        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePinSubmit} disabled={verifyingPin} startIcon={verifyingPin ? <CircularProgress size={16} /> : null}>
                        {verifyingPin ? 'Verifying…' : 'Unlock'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <ErrorBoundary>
            <DashboardWrapper>
                {/* Staff release for the fullscreen lock, opened by Ctrl+Shift+K.
                    Rendered here rather than returned early so the kiosk stays on
                    screen behind it: a customer who finds the combination sees a
                    passcode box over the store's own catalogue, not a way out.
                    TKT-2026-105376. */}
                <Dialog
                    open={unlockPromptOpen}
                    onClose={() => setUnlockPromptOpen(false)}
                    fullWidth
                    maxWidth='xs'
                >
                    <DialogTitle>Unlock Fullscreen (STAFF ONLY)</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            fullWidth
                            margin='dense'
                            label='6-digit passcode'
                            value={unlockPin}
                            onChange={(e) => setUnlockPin(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleUnlockSubmit(); }}
                            error={!!unlockError}
                            helperText={unlockError || 'Enter your kiosk passcode to take this kiosk out of fullscreen.'}
                            inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setUnlockPromptOpen(false)}>Cancel</Button>
                        <Button onClick={handleUnlockSubmit} disabled={verifyingUnlock} startIcon={verifyingUnlock ? <CircularProgress size={16} /> : null}>
                            {verifyingUnlock ? 'Checking…' : 'Unlock'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Drawer
                    variant={isDesktop ? 'persistent' : 'temporary'}
                    open={drawerOpen}
                    onClose={handleCloseFilterDrawer}
                    sx={filterDrawerSx}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                            <FilterSection
                                hasSearchTerm={Boolean(searchTerm)}
                                categoriesList={filteredCategoriesList}
                                groupsList={groupsList}
                                filterOptions={filterOptions}
                                registry={effectiveRegistry}
                                filterSettings={filterSettings}
                                selectedCategory={selectedCategory}
                                selectedGroup={selectedGroup}
                                filterValues={filterValues}
                                searchCardText={effectiveSearchCardText}
                                onCategoryChange={handleCategoryChange}
                                onGroupChange={handleGroupChange}
                                onFilterChange={handleFilterChange}
                                onSearchCardTextChange={handleSearchCardTextChange}
                                onClearFilters={handleClearFilters}
                                onClose={handleCloseFilterDrawer}
                                sortBy={sortBy}
                                onSortChange={handleSortChange}
                                buylistMode={kioskMode === 'buy'}
                            />
                        </Box>
                        {/* TKT-2026-104396: this was gated on isDesktop (>=1200px), so on
                            phones and tablets the drawer had no close control at all. */}
                        <Box sx={{ pt: 2 }}>
                            <Button variant='contained' fullWidth onClick={handleCloseFilterDrawer}>
                                {isDesktop ? 'Hide Filters' : 'Show Results'}
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                <Box
                    sx={(t) => ({
                        ml: isDesktop && drawerOpen ? `${CONFIG.DRAWER_WIDTH}px` : 0,
                        mr: isDesktop && cartDrawerOpen ? `${CONFIG.CART_DRAWER_WIDTH}px` : 0,
                        px: isTablet ? 1 : 0,
                        transition: t.transitions.create('margin', {
                            easing: t.transitions.easing.easeOut,
                            duration: t.transitions.duration.standard,
                        }),
                        position: 'relative',
                    })}
                >
                    <AppBar position='sticky' elevation={2}>
                        <Toolbar
                            variant='dense'
                            sx={{
                                minHeight: { xs: 68, sm: 56, lg: 76 },
                                alignItems: 'center',
                                gap: { xs: 1, sm: 1, lg: 1.5 },
                                flexWrap: 'wrap',
                                py: { xs: 0.5, sm: 0.5 },
                            }}
                        >
                            <IconButton
                                color='inherit'
                                edge='start'
                                onClick={() => setDrawerOpen((o) => !o)}
                                sx={{ mr: { xs: 0.5, lg: 1 }, minWidth: 44, minHeight: 44 }}
                                aria-label='Toggle filters'
                            >
                                <MenuIcon />
                            </IconButton>

                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                {storeLogoUrl ? (
                                    <Box component='img' src={storeLogoUrl} alt={`${headerStoreName} logo`} sx={{ height: { xs: 30, lg: 36 }, objectFit: 'contain', maxWidth: { xs: 120, sm: 160, lg: 220 } }} />
                                ) : (
                                    <Typography variant='h6' sx={{ fontSize: { xs: '0.95rem', lg: '1.1rem' }, fontWeight: 700, letterSpacing: '0.06em' }}>
                                        {headerStoreName.replace(/-/g, ' ').toUpperCase()}
                                    </Typography>
                                )}
                            </Box>

                            {/* Draws nothing unless the store's program shows balances on the kiosk. TKT-2026-106724 */}
                            <LoyaltyBalanceLookup storeName={storeName} />

                            <IconButton
                                color='inherit'
                                onClick={colorMode.toggleColorMode}
                                sx={{ minWidth: 44, minHeight: 44, order: { xs: 0, lg: 0 } }}
                                aria-label='Toggle theme'
                            >
                                {theme.palette.mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
                            </IconButton>

                            <IconButton
                                color='inherit'
                                onClick={() => setCartDrawerOpen(prev => !prev)}
                                sx={{ minWidth: 44, minHeight: 44, order: { xs: 0, lg: 0 } }}
                                aria-label={`Shopping cart with ${cart.length} items`}
                            >
                                <Badge badgeContent={cart.length} color='primary'>
                                    <ShoppingCartIcon />
                                </Badge>
                            </IconButton>

                            {/* Second row: toggles + search */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexBasis: { xs: '100%', lg: 'auto' },
                                flexGrow: { xs: 1, lg: 0 },
                                order: { xs: 1, lg: 0 },
                                flexWrap: 'wrap',
                                mt: { xs: 0.5, lg: 0 },
                            }}>
                                <ToggleButtonGroup
                                    value={productType}
                                    exclusive
                                    onChange={(_, v) => v && (setProductType(v), setPage(1))}
                                    size='small'
                                    aria-label='Product type'
                                    sx={(t) => ({
                                        borderRadius: 999,
                                        flexShrink: 0,
                                        backgroundColor: alpha(t.palette.common.white, t.palette.mode === 'light' ? 0.18 : 0.12),
                                        backdropFilter: 'blur(12px)',
                                        '& .MuiToggleButton-root': {
                                            border: 'none',
                                            padding: { xs: '6px 10px', lg: '6px 14px' },
                                            color: t.palette.mode === 'light' ? t.palette.common.white : t.palette.grey[300],
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            fontSize: { xs: '0.75rem', lg: '0.8125rem' },
                                            minHeight: 36,
                                        },
                                        '& .MuiToggleButton-root.Mui-selected': {
                                            backgroundColor: t.palette.common.white,
                                            // The pill is white whatever the store
                                            // configured, so the label needs the
                                            // readable-on-white primary rather than
                                            // the raw one. TKT-2026-105169.
                                            color: t.palette.primary.onLight,
                                            boxShadow: '0 16px 28px rgba(0,0,0,0.22)',
                                        },
                                    })}
                                >
                                    <ToggleButton value='all'>All</ToggleButton>
                                    <ToggleButton value='single'>Singles</ToggleButton>
                                    <ToggleButton value='sealed'>Sealed</ToggleButton>
                                    <ToggleButton value='custom'>Custom</ToggleButton>
                                </ToggleButtonGroup>

                                {hasBuylistSubscription && (
                                    <ToggleButtonGroup
                                        value={kioskMode}
                                        exclusive
                                        onChange={(_, v) => {
                                            if (v) {
                                                setKioskMode(v);
                                                if (v === 'buy') {
                                                    setInStockOnly(false);
                                                    if (selectedCategory && buylistEnabledCategoryIds.length > 0 &&
                                                        !buylistEnabledCategoryIds.includes(selectedCategory.categoryId)) {
                                                        setSelectedCategory(null);
                                                        setSelectedGroup(null);
                                                    }
                                                } else {
                                                    setInStockOnly(true);
                                                }
                                                setPage(1);
                                            }
                                        }}
                                        size='small'
                                        aria-label='Kiosk mode'
                                        sx={(t) => ({
                                            borderRadius: 999,
                                            flexShrink: 0,
                                            backgroundColor: kioskMode === 'buy'
                                                ? alpha(t.palette.success.main, 0.2)
                                                : alpha(t.palette.common.white, t.palette.mode === 'light' ? 0.18 : 0.12),
                                            backdropFilter: 'blur(12px)',
                                            border: kioskMode === 'buy' ? `2px solid ${t.palette.success.main}` : 'none',
                                            '& .MuiToggleButton-root': {
                                                border: 'none',
                                                padding: { xs: '6px 10px', lg: '6px 14px' },
                                                color: t.palette.mode === 'light' ? t.palette.common.white : t.palette.grey[300],
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                fontSize: { xs: '0.75rem', lg: '0.8125rem' },
                                                minHeight: 36,
                                            },
                                            '& .MuiToggleButton-root.Mui-selected': {
                                                backgroundColor: kioskMode === 'buy' ? t.palette.success.main : t.palette.common.white,
                                                // See the Singles/Sealed toggle above.
                                                // TKT-2026-105169.
                                                color: kioskMode === 'buy'
                                                    ? t.palette.success.contrastText
                                                    : t.palette.primary.onLight,
                                                boxShadow: '0 16px 28px rgba(0,0,0,0.22)',
                                            },
                                        })}
                                    >
                                        <ToggleButton value='sell'>Buy From Us</ToggleButton>
                                        <ToggleButton value='buy'>Sell To Us</ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            </Box>

                            <Box sx={{
                                flexBasis: { xs: '100%', lg: 'auto' },
                                flexGrow: 1,
                                order: { xs: 2, lg: 0 },
                                mt: { xs: 0.5, lg: 0 },
                            }}>
                                <SearchBox>
                                    <StyledAutocomplete
                                        freeSolo
                                        size='small'
                                        options={suggestionOptions}
                                        filterOptions={suggestionFilterOptions}
                                        loading={loadingSug}
                                        inputValue={searchTerm}
                                        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                                        onChange={(_, value) => {
                                            setSearchTerm(typeof value === 'string' ? value : value?.label || '');
                                            setPage(1);
                                        }}
                                        onInputChange={(_, v) => {
                                            setSearchTerm(v);
                                            setPage(1);
                                        }}
                                        sx={{ flex: 1 }}
                                        disableClearable
                                        renderOption={(props, option) => {
                                            const { key: optionKey, ...optionProps } = props;
                                            return (
                                                <Box component='li' {...optionProps} key={option.id || optionKey}>
                                                    {option.label}
                                                </Box>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder='Search products…'
                                                variant='standard'
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    sx: { color: 'inherit', minHeight: 36 },
                                                    startAdornment: <SearchIcon sx={{ mr: 1, opacity: 0.7 }} />,
                                                }}
                                            />
                                        )}
                                    />
                                    {searchTerm && (
                                        <IconButton
                                            onClick={() => {
                                                setSearchTerm('');
                                                setPage(1);
                                            }}
                                            sx={{ p: 0.5, color: 'inherit', minWidth: 36, minHeight: 36 }}
                                            aria-label='Clear search'
                                        >
                                            <ClearIcon fontSize='small' />
                                        </IconButton>
                                    )}
                                    {/* Search card text toggle (TKT-2026-102569) */}
                                    <Tooltip title={effectiveSearchCardText ? 'Also searching rules / ability text. Click to search names only.' : 'Searching names only. Click to also search rules / ability text.'}>
                                        <IconButton
                                            onClick={() => handleSearchCardTextChange(!effectiveSearchCardText)}
                                            sx={{
                                                p: 0.5,
                                                ml: 0.25,
                                                color: 'inherit',
                                                minWidth: 36,
                                                minHeight: 36,
                                                opacity: effectiveSearchCardText ? 1 : 0.55,
                                                bgcolor: effectiveSearchCardText ? (t) => alpha(t.palette.common.white, 0.22) : 'transparent',
                                            }}
                                            aria-label='Toggle search card text'
                                            aria-pressed={effectiveSearchCardText}
                                        >
                                            <NotesIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </SearchBox>
                            </Box>
                        </Toolbar>
                    </AppBar>

                    {/* Integrations, Kiosk, Welcome Message. Saved and served
                        since the field was added but never drawn anywhere, so
                        stores were filling it in and seeing nothing. Only
                        rendered when they have actually set one, so no kiosk
                        gains a strip it did not have. TKT-2026-105169. */}
                    {themeOverrides?.welcomeMessage && (
                        <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 2.5 } }}>
                            <Typography
                                variant='body2'
                                align='center'
                                color='text.secondary'
                                sx={{ fontWeight: 500 }}
                            >
                                {themeOverrides.welcomeMessage}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ px: { xs: 2, md: 4 }, mt: { xs: 3, md: 4 } }}>
                        <HeroPaper>
                            <Stack
                                direction={{ xs: 'column', lg: 'row' }}
                                spacing={3}
                                sx={{
                                    width: '100%',
                                    alignItems: { xs: 'stretch', lg: 'center' },
                                    justifyContent: 'space-between',
                                }}
                            >
                                {/* Filters & Controls Section */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    sx={{
                                        flex: { xs: '1', lg: '1' },
                                        flexWrap: 'wrap',
                                        justifyContent: { xs: 'flex-start', lg: 'flex-start' },
                                        alignItems: 'center',
                                    }}
                                >
                                    <Chip
                                        icon={<FilterListIcon fontSize='small' />}
                                        label={activeFiltersCount ? `${activeFiltersCount} Filter${activeFiltersCount > 1 ? 's' : ''}` : 'No Filters'}
                                        color={activeFiltersCount ? 'primary' : 'default'}
                                        variant={activeFiltersCount ? 'filled' : 'outlined'}
                                        onClick={() => setDrawerOpen(true)}
                                        sx={(theme) => ({
                                            px: 1.75,
                                            height: 36,
                                            fontWeight: 600,
                                            fontSize: '0.8125rem',
                                            borderRadius: theme.spacing(1.5),
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(62,172,253,0.25)',
                                            },
                                            ...(activeFiltersCount && {
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                '& .MuiChip-icon': {
                                                    color: theme.palette.primary.contrastText,
                                                },
                                            }),
                                        })}
                                    />
                                    <Chip
                                        label={`Sort: ${sortLabel}`}
                                        variant='outlined'
                                        onClick={() => setDrawerOpen(true)}
                                        sx={(theme) => ({
                                            px: 1.75,
                                            height: 36,
                                            fontWeight: 600,
                                            fontSize: '0.8125rem',
                                            borderRadius: theme.spacing(1.5),
                                            borderColor: alpha(theme.palette.primary.main, 0.3),
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(62,172,253,0.25)',
                                            },
                                        })}
                                    />
                                    <Chip
                                        label={productTypeLabel}
                                        color='primary'
                                        variant='filled'
                                        sx={(theme) => ({
                                            px: 1.75,
                                            height: 36,
                                            fontWeight: 600,
                                            fontSize: '0.8125rem',
                                            borderRadius: theme.spacing(1.5),
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.primary.contrastText,
                                        })}
                                    />
                                    {/* Hide In Stock filter in buy mode - all products shown */}
                                    {kioskMode !== 'buy' && (
                                        <Tooltip title={inStockOnly ? 'Currently showing only items that are available to purchase.' : 'Include out-of-stock products in the catalog view.'}>
                                            <Chip
                                                icon={<InventoryIcon />}
                                                label={inStockOnly ? 'In Stock' : 'All Items'}
                                                color={inStockOnly ? 'primary' : 'default'}
                                                variant={inStockOnly ? 'filled' : 'outlined'}
                                                onClick={() => {
                                                    setInStockOnly((prev) => !prev);
                                                    setPage(1);
                                                }}
                                                sx={(theme) => ({
                                                    px: 1.75,
                                                    height: 36,
                                                    fontWeight: 600,
                                                    fontSize: '0.8125rem',
                                                    borderRadius: theme.spacing(1.5),
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 4px 12px rgba(62,172,253,0.25)',
                                                    },
                                                    ...(inStockOnly && {
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: theme.palette.primary.contrastText,
                                                        '& .MuiChip-icon': {
                                                            color: theme.palette.primary.contrastText,
                                                        },
                                                    }),
                                                })}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip title={showCardScans ? 'Hide high-resolution card scans.' : 'Reveal high-resolution card scans for collectors and grading.'}>
                                        <Chip
                                            icon={<CardScanIcon />}
                                            label={showCardScans ? 'Scans On' : 'Scans Off'}
                                            color={showCardScans ? 'primary' : 'default'}
                                            variant={showCardScans ? 'filled' : 'outlined'}
                                            onClick={() => {
                                                setShowCardScans((s) => !s);
                                                setPage(1);
                                            }}
                                            sx={(theme) => ({
                                                px: 1.75,
                                                height: 36,
                                                fontWeight: 600,
                                                fontSize: '0.8125rem',
                                                borderRadius: theme.spacing(1.5),
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: '0 4px 12px rgba(62,172,253,0.25)',
                                                },
                                                ...(showCardScans && {
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    '& .MuiChip-icon': {
                                                        color: theme.palette.primary.contrastText,
                                                    },
                                                }),
                                            })}
                                        />
                                    </Tooltip>
                                </Stack>

                                {/* Stats Section */}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    sx={{
                                        flex: { xs: '1', lg: '0 0 auto' },
                                        minWidth: { lg: 'fit-content' },
                                    }}
                                >
                                    <StatPill>
                                        <Typography variant='overline' color='text.secondary' sx={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                                            Total Products
                                        </Typography>
                                        <Typography variant='h4' sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2rem' } }}>
                                            {total.toLocaleString()}
                                        </Typography>
                                        <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                                            {results.length} currently in view
                                        </Typography>
                                    </StatPill>
                                    <StatPill>
                                        <Typography variant='overline' color='text.secondary' sx={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>
                                            Cart Status
                                        </Typography>
                                        <Typography variant='h4' sx={{ fontWeight: 700, fontSize: { xs: '1.75rem', md: '2rem' } }}>
                                            {cart.length}
                                        </Typography>
                                        <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                                            {formatMoney(totalPrice)} subtotal
                                        </Typography>
                                    </StatPill>
                                </Stack>
                            </Stack>

                            {/* Applied-filters chip bar (each removable). TKT-2026-102569 */}
                            {appliedFilterChips.length > 0 && (
                                <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    {appliedFilterChips.map((chip) => (
                                        <Chip
                                            key={chip.id}
                                            label={chip.label}
                                            size='small'
                                            onDelete={chip.onDelete}
                                            deleteIcon={<CloseIcon fontSize='small' />}
                                            sx={(t) => ({
                                                height: 30,
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                borderRadius: t.spacing(1.25),
                                                backgroundColor: alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.12 : 0.24),
                                            })}
                                        />
                                    ))}
                                    <Button
                                        size='small'
                                        startIcon={<CloseIcon fontSize='small' />}
                                        onClick={handleClearFilters}
                                        sx={{ borderRadius: 12, textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Clear All
                                    </Button>
                                </Box>
                            )}
                        </HeroPaper>
                    </Box>

                    <Container maxWidth='xl' sx={{ py: { xs: 4, md: 6 }, pb: { xs: 8, md: 10 } }}>
                        {error && (
                            <Paper
                                sx={(t) => ({
                                    mb: 3,
                                    p: { xs: 2, md: 3 },
                                    border: `1px solid ${alpha(t.palette.error.main, 0.25)}`,
                                    background: alpha(t.palette.error.main, 0.1),
                                })}
                            >
                                <Typography variant='subtitle1' color='error.main' fontWeight={600}>
                                    {error}
                                </Typography>
                                <Typography variant='body2' color='error.main'>
                                    Please refresh or adjust your filters and try again.
                                </Typography>
                            </Paper>
                        )}

                        {loading ? (
                            <ProductGrid>
                                {Array.from({ length: CONFIG.PAGE_SIZE }).map((_, i) => (
                                    <Skeleton key={i} variant='rounded' height={isMobile ? 280 : 330} sx={{ borderRadius: 2, transform: 'scale(1, 0.98)' }} />
                                ))}
                            </ProductGrid>
                        ) : (
                            <>
                                {/* The search was answered for a corrected spelling
                                    (utils/searchCorrection.js). Plain text only. */}
                                {searchCorrection && results.length > 0 && (
                                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }} data-testid='search-correction'>
                                        {formatSearchCorrection(searchCorrection)}
                                    </Typography>
                                )}
                                <ResultsGrid
                                    results={results}
                                    kioskMode={kioskMode}
                                    storeName={storeName}
                                    showCardScans={showCardScans}
                                    stockDisplayMode={themeOverrides?.stockDisplayMode || 'exact'}
                                    onAdd={handleAddToCart}
                                    onImageClick={handleOpenImageZoom}
                                    onProductClick={handleOpenProduct}
                                    buylistGameSettings={buylistGameSettings}
                                    gradedCategoryIds={gradedCategoryIds}
                                />

                                {loadingMore && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                        <CircularProgress size={40} />
                                    </Box>
                                )}

                                {results.length === 0 && (
                                    <Paper sx={{ textAlign: 'center', py: 8, mt: 4 }}>
                                        {(kioskMode === 'buy' || (kioskMode === 'sell' && !inStockOnly)) && !searchTerm?.trim() ? (
                                            <>
                                                <Typography variant='h6' color='text.secondary' gutterBottom>
                                                    {kioskMode === 'buy' ? 'Search to Sell Your Cards' : 'Search to Find Products'}
                                                </Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    {kioskMode === 'buy'
                                                        ? "Use the search bar above to find products you'd like to sell to us."
                                                        : "Use the search bar above to search our full catalog."}
                                                </Typography>
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant='h6' color='text.secondary' gutterBottom>
                                                    No products found
                                                </Typography>
                                                <Typography variant='body2' color='text.secondary'>
                                                    Try adjusting your search, filters, or availability settings.
                                                </Typography>
                                            </>
                                        )}
                                    </Paper>
                                )}
                            </>
                        )}

                        <Box sx={{ height: 1 }} />
                        <div ref={attachLoadMore} />
                    </Container>

                    <CartDrawer
                        open={cartDrawerOpen}
                        onClose={handleCloseCartDrawer}
                        cart={cart}
                        totalPrice={totalPrice}
                        navigate={navigate}
                        storeName={storeName}
                        highlightSku={highlightSku}
                        isDesktop={isDesktop}
                        isTablet={isTablet}
                        onRemove={removeFromCart}
                        onUpdateQty={updateQuantity}
                        buylistDisclaimer={themeOverrides?.buylistDisclaimer}
                        salesTaxRate={themeOverrides?.salesTaxRate || 0}
                        taxOnNetTotal={themeOverrides?.taxOnNetTotal || false}
                        taxInclusive={themeOverrides?.taxInclusive || false}
                    />

                    <Suspense fallback={<Skeleton variant='rectangular' height={400} />}>
                        {selectedProd && (
                            <DetailDialog
                                open={detailOpen}
                                product={selectedProd}
                                showCardScans={showCardScans}
                                onClose={() => setDetailOpen(false)}
                                storeName={storeName}
                                showCardLocations={themeOverrides?.showCardLocations === true}
                                stockDisplayMode={themeOverrides?.stockDisplayMode || 'exact'}
                                cartOptimization={themeOverrides?.cartOptimization || null}
                                buylistGameSettings={buylistGameSettings}
                                gradedEnabled={isGradedCategory(gradedCategoryIds, selectedProd.categoryId)}
                                onAdd={(p, variant, buylistOptions) => {
                                    handleAddToCart(p, variant || p.variants?.[0] || null, buylistOptions);
                                    setDetailOpen(false);
                                }}
                            />
                        )}
                    </Suspense>

                    <Dialog open={Boolean(zoomImage)} onClose={handleCloseImageZoom} maxWidth='md' fullWidth>
                        <DialogTitle>{zoomTitle}</DialogTitle>
                        <DialogContent dividers>
                            {zoomImage ? (
                                <Box
                                    component='img'
                                    src={zoomBackImage && zoomSide === 'back' ? zoomBackImage : zoomImage}
                                    alt={zoomBackImage && zoomSide === 'back' ? `${zoomTitle} (back)` : zoomTitle}
                                    // Tapping the enlarged card turns it over when it has a back scan.
                                    onClick={zoomBackImage ? () => setZoomSide((s) => (s === 'back' ? 'front' : 'back')) : undefined}
                                    sx={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', cursor: zoomBackImage ? 'pointer' : 'default' }}
                                />
                            ) : null}
                        </DialogContent>
                        <DialogActions sx={{ gap: 1, flexWrap: 'wrap' }}>
                            {/* TKT-2026-105491: only for a copy with a back scan, Scans On. */}
                            {zoomBackImage && (
                                <ToggleButtonGroup
                                    value={zoomSide}
                                    exclusive
                                    aria-label='Card side'
                                    onChange={(_, side) => side && setZoomSide(side)}
                                    sx={(t) => ({
                                        mr: 'auto',
                                        '& .MuiToggleButton-root': {
                                            minHeight: 48,
                                            minWidth: 96,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                        },
                                        '& .MuiToggleButton-root.Mui-selected': {
                                            backgroundColor: t.palette.primary.main,
                                            color: t.palette.primary.contrastText,
                                            '&:hover': { backgroundColor: t.palette.primary.dark },
                                        },
                                    })}
                                >
                                    <ToggleButton value='front'>Front</ToggleButton>
                                    <ToggleButton value='back'>Back</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                            <Button onClick={handleCloseImageZoom}>Close</Button>
                        </DialogActions>
                    </Dialog>

                    <Fab
                        size='small'
                        color='primary'
                        onClick={handleScrollTop}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            display: showTop ? 'inline-flex' : 'none',
                            transition: 'transform 0.3s',
                            '&:hover': { transform: 'scale(1.1)' },
                        }}
                        aria-label='Scroll to top'
                    >
                        <KeyboardArrowUpIcon />
                    </Fab>
                </Box>
            </DashboardWrapper>
        </ErrorBoundary>
    );
}

Dashboard.propTypes = {
    storeName: PropTypes.string,
};
